/**
 * Mock data for the Manufacturing setup admin surface (design note
 * `docs/features/cost-engine-productionisation/03_manufacturing_surfaces_design.md`).
 *
 * Every code, site, rate and standards row below is drawn from the frozen
 * Tharstern standards snapshot at
 * `docs/features/cost-engine/analysis/oracle/data/standards/*.csv`
 * (captured 2026-09-08 from the `Thardata` production replica) — real cost
 * centre codes, real sites, real rates, real materials. No customer data.
 * A representative "hero" subset of 13 cost centres, 6 ink profiles, 4
 * fully-replicated product types (plus two, 77 · Letterheads and 91 ·
 * Greeting cards, deliberately left "not yet replicated" to show that
 * state honestly — 91 also keeps Live costing's dict-greeting-cards
 * fixture from pointing at a product type this mirror doesn't have),
 * 13 materials
 * and 6 outwork choices carry full sub-tab detail; the remaining cost
 * centres from the snapshot appear in the list with header/gate data
 * only, so search and paging behave like a real 49-row table. 11 of the
 * 13 materials are the oracle snapshot's own rows (their Group/Type show
 * "—": the snapshot's `product_substrates.csv` never captured those two
 * columns, an honest gap rather than a fabricated classification); the
 * other two — a WINWINWIN house paper and a reel-fed vinyl — are added
 * for "Read it as Henry" review coverage of the Group/Type columns and
 * the reel-fed (no sheet sizes, microns-specified) shape.
 *
 * Every field badge comes from the `ENGINE_USE_MAP` below — never hand-set
 * per fixture — so a correction to the map is a correction everywhere at
 * once. This map used to be a shared `domain/manufacturing-engine-use-map.ts`
 * file; it retired (cost-engine-productionisation spec 07 §9) once the real
 * `/views/manufacturing/...` endpoints started badging every field
 * server-side from the engine's own field-usage map, so pages and the real
 * adapter never read it. It survives here, colocated with its one remaining
 * consumer — generating this mock data's own badges — rather than as a
 * standalone shared file with a single caller.
 */

import type { ListOptions, ListResponse } from "../data/adapter";
import type {
  CostCentreDetail,
  CostCentreIndexEntry,
  CostCentreListOptions,
  EngineUseField,
  EngineUseRow,
  InkProfileDetail,
  InkProfileIndexEntry,
  InkProfileListOptions,
  ManufacturingPressConsidered,
  ManufacturingSyncInfo,
  MaterialDetail,
  MaterialIndexEntry,
  MaterialListOptions,
  MaterialSheetSize,
  OutworkChoiceEntry,
  OutworkListOptions,
  ProcessTypeEntry,
  ProductTypeDetail,
  ProductTypeIndexEntry,
  ProductTypeListOptions,
  SiteEntry,
} from "../data/manufacturing-setup-types";

// ── Engine field-usage map — mock-fixture support only ────────────────────
// Generated from the engine's own code (`modules/cost_engine_module/models/
// cost_standards.py`, `data_adapters/postgres_ro.py` loaders) and
// `docs/features/cost-engine/30_maintenance_and_rolling_validation.md` §1 /
// `99_outstanding_setup.md` §1a — never hand-guessed per entity. The real
// `/views/manufacturing/...` endpoints badge every field server-side from
// this same engine field-usage map, so pages and the real adapter never read
// this table; it exists purely so this mock data's fixtures stay internally
// consistent (one correction to the map is a correction everywhere at once).

interface EngineUseMapEntry {
  status: EngineUseField["status"];
  /** Shown when status isn't "used_by_engine" and the reason isn't
   *  self-evident from the label alone. */
  why?: string;
}

type EngineUseFamily =
  | "cost_centre"
  | "cost_centre_gate"
  | "rate"
  | "run_speed"
  | "setup"
  | "run"
  | "geometry"
  | "packing"
  | "ink_profile"
  | "product_type"
  | "product_type_part"
  | "step_map_link"
  | "material"
  | "material_sheet_size"
  | "outwork_choice";

const NOT_YET_REPLICATED: EngineUseMapEntry = { status: "not_yet_replicated" };

/**
 * Per-family, per-field status. A field absent from a family's map falls
 * back to "not_yet_replicated" via `engineUseFor` below — an unlisted field
 * is a gap, not a silent "used".
 *
 * Verified 2026-09-12 against `modules/cost_engine_module/models/
 * cost_standards.py` and `data_adapters/postgres_ro.py` (the only loader —
 * confirmed by a full read of the 603-line file). Two families turned out
 * to have **no loader at all** despite being fully replicated: cost centre
 * geometry (a margin decode was proven exact on a subset of presses, then
 * deliberately left unwired because it's falsified on other real jobs) and
 * ink profiles (the engine substitutes its own hard-coded per-profile ink
 * constants instead of reading `InkProfile`/`SuitableInkCostCentres` at
 * all). The design note's own worked example (`03_manufacturing_surfaces_
 * design.md`) guessed `max_inline_gsm` as "not yet replicated" — it is
 * already replicated on the raw `CostCentres` view, just not selected by
 * the loader, so it's corrected here to "replicated, not used".
 */
const ENGINE_USE_MAP: Record<
  EngineUseFamily,
  Record<string, EngineUseMapEntry>
> = {
  cost_centre: {
    code: { status: "used_by_engine" },
    process_type_id: { status: "used_by_engine" },
    res_type: {
      status: "used_by_engine",
      why: "Jointly with process_type_id, selects the finishing-input quantity family.",
    },
    is_reel_fed: { status: "used_by_engine" },
    speed: { status: "used_by_engine" },
    site: {
      status: "replicated_not_used",
      why: "Replicated on CostCentres, but not selected by the standards loader yet — site-based routing is an agreed future policy, not implemented in the engine today.",
    },
    single_plate: {
      status: "replicated_not_used",
      why: "Plate count is derived from the spec's own ink fields, not this flag.",
    },
    incremental_spoils: {
      status: "replicated_not_used",
      why: "No cost-computation code reads this column today.",
    },
    click_per_colour: {
      status: "replicated_not_used",
      why: "Loaded, but the digital-click cost formula doesn't touch it.",
    },
    rate: {
      status: "replicated_not_used",
      why: "Superseded by the MachinesAndLabour charge-rate rows.",
    },
  },
  cost_centre_gate: {
    min_width: {
      status: "replicated_not_used",
      why: "Replicated on CostCentres, not yet selected by the standards loader.",
    },
    max_width: {
      status: "replicated_not_used",
      why: "Replicated on CostCentres, not yet selected by the standards loader.",
    },
    min_depth: {
      status: "replicated_not_used",
      why: "Replicated on CostCentres, not yet selected by the standards loader.",
    },
    max_depth: {
      status: "replicated_not_used",
      why: "Replicated on CostCentres, not yet selected by the standards loader.",
    },
    min_caliper: {
      status: "replicated_not_used",
      why: "Replicated on CostCentres, not yet selected by the standards loader.",
    },
    max_caliper: {
      status: "replicated_not_used",
      why: "Replicated on CostCentres, not yet selected by the standards loader.",
    },
    scheduling: {
      status: "replicated_not_used",
      why: "A Tharstern scheduling-board flag, not a costing input.",
    },
    stations: {
      status: "replicated_not_used",
      why: "Not a colour ceiling — route-candidate validation found a 4-colour job running on a 2-station press. Colour count comes from the ink profile instead.",
    },
    max_inline_gsm: {
      status: "replicated_not_used",
      why: "Replicated on CostCentres, not yet selected by the standards loader.",
    },
  },
  rate: {
    price: { status: "used_by_engine" },
    labour: { status: "used_by_engine" },
    min_charge: { status: "used_by_engine" },
    timedb: { status: "used_by_engine" },
    run_speed: { status: "used_by_engine" },
    pile_height: {
      status: "replicated_not_used",
      why: "Tharstern's own owner confirmed this is a red herring — superseded by DFProcessRun's pile-height band.",
    },
  },
  run_speed: {
    weight: { status: "used_by_engine" },
    working_size_code: { status: "used_by_engine" },
    up_to_working_size: { status: "used_by_engine" },
    duration_band: { status: "used_by_engine" },
    run_speed: { status: "used_by_engine" },
    spoils_percentage: { status: "used_by_engine" },
    fold_type: { status: "used_by_engine" },
  },
  setup: {
    setup_type: { status: "used_by_engine" },
    quantity_factor: { status: "used_by_engine" },
    duration: { status: "used_by_engine" },
    quantity_spoils: { status: "used_by_engine" },
    print_method: { status: "used_by_engine" },
    fold_type: { status: "used_by_engine" },
  },
  run: {
    quantity: { status: "used_by_engine" },
    weight: { status: "used_by_engine" },
    basic_sizes_id: {
      status: "replicated_not_used",
      why: "Captured for fidelity, but not yet wired as a selection axis in the engine.",
    },
    run_speed: { status: "used_by_engine" },
    duration: { status: "used_by_engine" },
  },
  // No loader reads CostCentreGeometry at all: a margin decode
  // (grip + back-edge + colour-bar allowances) was proven exact on a
  // subset of presses, then deliberately left unwired because it's
  // falsified on other real jobs. The whole family is replicated but
  // unused, not partially used.
  geometry: {
    print_method: {
      status: "replicated_not_used",
      why: "A margin decode from this table proved exact on a subset of presses, then was deliberately left unwired — it doesn't hold across every real job.",
    },
    grip_left: {
      status: "replicated_not_used",
      why: "Same unwired margin decode as the other geometry fields.",
    },
    grip_right: {
      status: "replicated_not_used",
      why: "Same unwired margin decode as the other geometry fields.",
    },
    back_edge: {
      status: "replicated_not_used",
      why: "Same unwired margin decode as the other geometry fields.",
    },
    grip_edge: {
      status: "replicated_not_used",
      why: "Same unwired margin decode as the other geometry fields.",
    },
    colour_bar: {
      status: "replicated_not_used",
      why: "Same unwired margin decode as the other geometry fields.",
    },
    colour_bar_position: {
      status: "replicated_not_used",
      why: "Replicated alongside the rest of this table; the margin decode it would refine was never wired in.",
    },
    type: {
      status: "replicated_not_used",
      why: "Used only inside the rejected decode as a row filter, never reaches production.",
    },
  },
  packing: {
    box_quantity: { status: "used_by_engine" },
    box_units_type: { status: "used_by_engine" },
    finished_size_code_from: { status: "used_by_engine" },
    finished_size_code_to: { status: "used_by_engine" },
    box_product_id: {
      status: "used_by_engine",
      why: "Used only as a fallback when the step's own material code doesn't resolve.",
    },
  },
  // No loader reads InkProfile or SuitableInkCostCentres at all: the
  // engine substitutes its own hard-coded per-profile-name ink constants
  // (price/coverage/density) instead. See CostCentreDetail.engineConstants
  // for where those constants show up on the ink-profile page.
  ink_profile: {
    process_inks: {
      status: "replicated_not_used",
      why: "No ink-profile loader exists yet — the engine substitutes its own hard-coded per-profile ink constants for this table entirely.",
    },
    spot_inks: {
      status: "replicated_not_used",
      why: "No ink-profile loader exists yet — the engine substitutes its own hard-coded per-profile ink constants for this table entirely.",
    },
    metallic_inks: {
      status: "replicated_not_used",
      why: "No ink-profile loader exists yet — the engine substitutes its own hard-coded per-profile ink constants for this table entirely.",
    },
    coatings: {
      status: "replicated_not_used",
      why: "No ink-profile loader exists yet — the engine substitutes its own hard-coded per-profile ink constants for this table entirely.",
    },
    is_legacy: {
      status: "replicated_not_used",
      why: "No ink-profile loader exists yet.",
    },
    quantity: {
      status: "replicated_not_used",
      why: "No ink-profile loader exists yet.",
    },
    resource_type: {
      status: "replicated_not_used",
      why: "A Tharstern scheduling resource pool, not a costing input, and no loader reads it regardless.",
    },
    default_for_colours: {
      status: "replicated_not_used",
      why: "Drives Tharstern's own UI defaulting only.",
    },
  },
  product_type: {
    order_expense: { status: "used_by_engine" },
    type: {
      status: "used_by_engine",
      why: "Gates the bound-book text-block precut rule.",
    },
  },
  // tharstern_product_type_parts has no loader in postgres_ro.py at all.
  // default_orientation / optimise_sheet / gutter_horizontal /
  // gutter_vertical / imposition_proofing_named_feature_id aren't even
  // columns on the production replica's view (gutter values are read from
  // a different, per-job table — EstimatePart — which isn't replicated
  // for this purpose either).
  product_type_part: {
    is_no_printing: {
      status: "replicated_not_used",
      why: "product_type_parts isn't loaded into the engine's standards yet.",
    },
    default_printing_cost_centre_id: {
      status: "replicated_not_used",
      why: "product_type_parts isn't loaded into the engine's standards yet.",
    },
    default_orientation: {
      status: "not_yet_replicated",
      why: "Imposition derives orientation from spec geometry; this column isn't on the production replica.",
    },
    optimise_sheet: NOT_YET_REPLICATED,
    gutter_horizontal: {
      status: "not_yet_replicated",
      why: "Read from EstimatePart on a per-job basis when supplied directly — not from this table, and not replicated for that purpose.",
    },
    gutter_vertical: {
      status: "not_yet_replicated",
      why: "Read from EstimatePart on a per-job basis when supplied directly — not from this table, and not replicated for that purpose.",
    },
    imposition_proofing_named_feature_id: NOT_YET_REPLICATED,
  },
  // default_cost_centre_id / default_outwork_id / default_material_code /
  // default_quantity live on ProductTypeProcessTypeLinks, which IS loaded.
  // is_required / is_default / line_number are on the same Tharstern table
  // but the loader's SELECT doesn't carry them yet. allow_auto_selection /
  // auto_selects_cheapest belong to a different table (the part-level
  // links) with no loader at all.
  step_map_link: {
    default_outwork_id: { status: "used_by_engine" },
    default_material_code: { status: "used_by_engine" },
    default_cost_centre_id: {
      status: "replicated_not_used",
      why: "Loaded, but no action reads it — the engine chooses among candidate cost centres by enumeration, not this stored default.",
    },
    default_quantity: {
      status: "replicated_not_used",
      why: "Quantity comes from the job spec, not this Tharstern default.",
    },
    is_required: {
      status: "not_yet_replicated",
      why: "On Tharstern's own link table, but not yet selected by the standards loader.",
    },
    is_default: {
      status: "not_yet_replicated",
      why: "On Tharstern's own link table, but not yet selected by the standards loader.",
    },
    allow_auto_selection: {
      status: "not_yet_replicated",
      why: "Belongs to the part-level process-type links, which have no loader yet.",
    },
    auto_selects_cheapest: {
      status: "not_yet_replicated",
      why: "Belongs to the part-level process-type links, which have no loader yet.",
    },
  },
  // "product_substrates" and "product_stock_sizes" are, in this codebase,
  // one Tharstern table (Product / tharstern_products) — there is no
  // separate substrate/stock-size split.
  material: {
    unit_price: { status: "used_by_engine" },
    calliper: { status: "used_by_engine" },
    pricing_type: { status: "used_by_engine" },
    width_mm: { status: "used_by_engine" },
    depth_mm: { status: "used_by_engine" },
    use_full_pack: { status: "used_by_engine" },
    pack_quantity: { status: "used_by_engine" },
    min_charge: { status: "used_by_engine" },
    weight: {
      status: "replicated_not_used",
      why: "Replicated as Product.Weight, but not selected by the products loader — the engine takes gsm from the job spec, never the material master.",
    },
    gsm: {
      status: "replicated_not_used",
      why: 'Same Product.Weight column as "weight" above — replicated, not selected by the loader.',
    },
    inactive: {
      status: "replicated_not_used",
      why: "Replicated, but not selected by the products loader — availability isn't read from this flag today.",
    },
    group: {
      status: "replicated_not_used",
      why: "Replicated for the product catalogue's own material classification; the cost engine prices from weight/calliper/price instead.",
    },
    type: {
      status: "replicated_not_used",
      why: "Replicated for the product catalogue's own material classification; the cost engine prices from weight/calliper/price instead.",
    },
  },
  material_sheet_size: {
    width_mm: { status: "used_by_engine" },
    depth_mm: { status: "used_by_engine" },
    calliper: { status: "used_by_engine" },
    pack_quantity: { status: "used_by_engine" },
    use_full_pack: { status: "used_by_engine" },
    min_charge: { status: "used_by_engine" },
    inactive: {
      status: "replicated_not_used",
      why: "Replicated, but not selected by the products loader.",
    },
  },
  outwork_choice: {
    min_charge: { status: "used_by_engine" },
    additional_charge: { status: "used_by_engine" },
    price: { status: "used_by_engine" },
    click_price: {
      status: "replicated_not_used",
      why: "Outwork choices are costed from their own price bands; the supplier's click rate is a Tharstern purchasing reference, not a costing input here.",
    },
  },
};

/** Look up one field's engine-use status. An unlisted field defaults to
 *  "not_yet_replicated" — an omission from the map is a gap to close, not a
 *  silent "used". */
function engineUseFor(
  family: EngineUseFamily,
  field: string,
): EngineUseMapEntry {
  return ENGINE_USE_MAP[family][field] ?? NOT_YET_REPLICATED;
}

// ── Small helpers ──────────────────────────────────────────────────────────

function field(
  family: EngineUseFamily,
  key: string,
  label: string,
  value: string,
): EngineUseField {
  return { key, label, value, ...engineUseFor(family, key) };
}

/** A tabular row's badge is the worst (most gap-indicating) status among
 *  its constituent Tharstern fields — the engine consumes these rows as a
 *  unit, not field by field (types file's `EngineUseRow` docstring). */
const STATUS_RANK = {
  used_by_engine: 0,
  replicated_not_used: 1,
  not_yet_replicated: 2,
} as const;

function row(
  family: EngineUseFamily,
  fieldKeys: string[],
  cells: { label: string; value: string }[],
): EngineUseRow {
  let status: EngineUseRow["status"] = "used_by_engine";
  let why: string | undefined;
  for (const key of fieldKeys) {
    const entry = engineUseFor(family, key);
    if (STATUS_RANK[entry.status] > STATUS_RANK[status]) {
      status = entry.status;
      why = entry.why;
    }
  }
  return { cells, status, why };
}

function money(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

function moneyPerHour(pence: number): string {
  return `${money(pence)}/h`;
}

function paginate<T>(
  items: T[],
  options: ListOptions | undefined,
): ListResponse<T> {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 25;
  const total = items.length;
  const start = (page - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    total,
    page,
    pageSize,
    totalPages: total === 0 ? 0 : Math.ceil(total / pageSize),
  };
}

function sortBy<T>(
  items: T[],
  sort: string | undefined,
  keyFns: Record<string, (t: T) => string | number>,
  defaultField: string,
): T[] {
  const tokens = (sort ?? defaultField)
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
  let out = [...items];
  for (const token of [...tokens].reverse()) {
    const desc = token.startsWith("-");
    const name = token.replace(/^[+-]/, "");
    const keyFn = keyFns[name];
    if (!keyFn) throw new Error(`unknown sort field: ${name}`);
    out = [...out].sort((a, b) => {
      const ka = keyFn(a);
      const kb = keyFn(b);
      const cmp = ka < kb ? -1 : ka > kb ? 1 : 0;
      return desc ? -cmp : cmp;
    });
  }
  return out;
}

// ── Sync timestamps — "synced HH:MM from `<Tharstern table>`" ─────────────

const SYNC: Record<string, ManufacturingSyncInfo> = {
  costCentres: { table: "CostCentres", syncedAt: "2026-09-12T03:12:00Z" },
  rates: { table: "MachinesAndLabour", syncedAt: "2026-09-12T03:12:00Z" },
  runSpeeds: {
    table: "CostCentreRunSpeedLinks",
    syncedAt: "2026-09-12T02:40:00Z",
  },
  setupsRuns: {
    table: "DFProcessSetup / DFProcessRun",
    syncedAt: "2026-09-12T02:40:00Z",
  },
  geometry: { table: "CostCentreGeometry", syncedAt: "2026-09-11T22:05:00Z" },
  packing: { table: "PackingAttributes", syncedAt: "2026-09-11T22:05:00Z" },
  inkProfiles: { table: "InkProfile", syncedAt: "2026-09-12T03:12:00Z" },
  productTypes: {
    table: "ProductTypeProcessTypeLinks",
    syncedAt: "2026-09-10T04:00:00Z",
  },
  materials: { table: "Product", syncedAt: "2026-09-12T03:12:00Z" },
  outwork: { table: "OutworkChoices", syncedAt: "2026-09-11T22:05:00Z" },
  sites: { table: "LocationSites", syncedAt: "2026-09-01T04:00:00Z" },
  processTypes: { table: "ProcessTypes", syncedAt: "2026-09-01T04:00:00Z" },
};

// ── Process types (real, from the snapshot) ────────────────────────────────
// costCentreCount is the real count of cost centres gated to this process
// type across the full 1,267-row CostCentreGates snapshot.

// `_self`/`type` are additive self-describing-response fields (spec 05 §9.1)
// applied uniformly by `processTypeEntry` below, so the 40+ raw fixtures here
// stay free of repeated boilerplate.
const PROCESS_TYPES: Omit<ProcessTypeEntry, "_self" | "type">[] = [
  {
    processTypeId: 53,
    name: "Litho Printing",
    description: "",
    costCentreCount: 28,
    kind: "printing",
  },
  {
    processTypeId: 24,
    name: "Digital Printing",
    description: "",
    costCentreCount: 28,
    kind: "printing",
  },
  {
    processTypeId: 36,
    name: "Guillotine",
    description: "",
    costCentreCount: 19,
  },
  { processTypeId: 30, name: "Folding", description: "", costCentreCount: 10 },
  {
    processTypeId: 125,
    name: "Duplexing",
    description: "",
    costCentreCount: 2,
  },
  { processTypeId: 59, name: "Packing", description: "", costCentreCount: 21 },
  {
    processTypeId: 96,
    name: "Cut/Crease/Perf- Select Digital/Litho-Add Stripout Cost",
    description: "",
    costCentreCount: 30,
  },
  {
    processTypeId: 65,
    name: "Perfect Binding",
    description: "",
    costCentreCount: 13,
  },
  {
    processTypeId: 92,
    name: "Wiro Binding",
    description: "",
    costCentreCount: 7,
  },
  {
    processTypeId: 82,
    name: "Stitching",
    description: "",
    costCentreCount: 22,
  },
  {
    processTypeId: 52,
    name: "Laminating",
    description: "",
    costCentreCount: 47,
  },
  {
    processTypeId: 81,
    name: "Spot UV Varnishing",
    description: "",
    costCentreCount: 0,
  },
  {
    processTypeId: 110,
    name: "Large Format",
    description: "",
    costCentreCount: 14,
    kind: "printing",
  },
  {
    processTypeId: 139,
    name: "Cut to Shape",
    description: "",
    costCentreCount: 14,
  },
  {
    processTypeId: 188,
    name: "Profile Cutting",
    description: "",
    costCentreCount: 12,
  },
  {
    processTypeId: 126,
    name: "Laser Cutting",
    description: "",
    costCentreCount: 0,
  },
  { processTypeId: 72, name: "Pre-Trim", description: "", costCentreCount: 1 },
  {
    processTypeId: 87,
    name: "Trim Before Folding",
    description: "",
    costCentreCount: 0,
  },
  {
    processTypeId: 17,
    name: "Collating - Sheets",
    description: "",
    costCentreCount: 2,
  },
  {
    processTypeId: 141,
    name: "Touch Enhancement",
    description: "",
    costCentreCount: 12,
  },
  {
    processTypeId: 6,
    name: "Additional Sheets - Material Only",
    description: "",
    costCentreCount: 9,
  },
  {
    processTypeId: 89,
    name: "Variable Data Processing - Digital Printing Only",
    description: "",
    costCentreCount: 1,
  },
  {
    processTypeId: 95,
    name: "XL75 Folex",
    description: "",
    costCentreCount: 1,
  },
  {
    processTypeId: 97,
    name: "Hard Copy Proofs-Add Carriage",
    description: "",
    costCentreCount: 1,
  },
  {
    processTypeId: 2,
    name: "Plate Making",
    description: "",
    costCentreCount: 2,
  },
  {
    processTypeId: 88,
    name: "Trimming Covers",
    description: "",
    costCentreCount: 1,
  },
  {
    processTypeId: 66,
    name: "Perforating",
    description: "",
    costCentreCount: 1,
  },
  {
    processTypeId: 31,
    name: "Forms Collate",
    description: "",
    costCentreCount: 1,
  },
  {
    processTypeId: 39,
    name: "Handwork - Folder",
    description: "",
    costCentreCount: 1,
  },
  {
    processTypeId: 91,
    name: "Wide Format Printing",
    description: "",
    costCentreCount: 2,
    kind: "printing",
  },
  // The remaining process types referenced by cost centres in the list
  // below but not otherwise detailed on a hero cost centre's step map —
  // still named from the real snapshot so no cost centre falls back to a
  // bare numeric "Process type N" label.
  {
    processTypeId: 33,
    name: "Glueing pads + Backing Board",
    description: "",
    costCentreCount: 3,
  },
  {
    processTypeId: 94,
    name: "Writing Shields",
    description: "",
    costCentreCount: 2,
  },
  {
    processTypeId: 70,
    name: "Pop Up Banner Trim/Assemble/Pack (800 - 1000mm)",
    description: "",
    costCentreCount: 1,
  },
  {
    processTypeId: 64,
    name: "PDF Proofing",
    description: "",
    costCentreCount: 1,
  },
  {
    processTypeId: 50,
    name: "Hohner - Stitching Only",
    description: "",
    costCentreCount: 3,
  },
  {
    processTypeId: 19,
    name: "Counting / Insert Boards",
    description: "",
    costCentreCount: 1,
  },
  {
    processTypeId: 35,
    name: "Glueing Sets",
    description: "",
    costCentreCount: 3,
  },
  { processTypeId: 127, name: "Pre Cut", description: "", costCentreCount: 2 },
  {
    processTypeId: 187,
    name: "BCQ Display Proofing",
    description: "",
    costCentreCount: 1,
  },
  {
    processTypeId: 25,
    name: "Drilling - Final Product",
    description: "",
    costCentreCount: 7,
  },
];

const PROCESS_TYPE_NAMES = new Map(
  PROCESS_TYPES.map((p) => [p.processTypeId, p.name]),
);

// ── Sites (real, LocationSites) ────────────────────────────────────────────

const SITES: Omit<SiteEntry, "_self" | "type">[] = [
  {
    siteId: 1,
    code: "EXETER01",
    description: "Exeter - Oak House",
    isDefaultForPurchaseOrders: false,
    isDefaultDeliveryNoteFromAddress: false,
  },
  {
    siteId: 2,
    code: "BUCKINGHAM01",
    description: "Buckingham - Osier Way",
    isDefaultForPurchaseOrders: false,
    isDefaultDeliveryNoteFromAddress: false,
  },
  {
    siteId: 3,
    code: "PLYMOUTH01",
    description: "Plymouth - Sisna Park Road",
    isDefaultForPurchaseOrders: false,
    isDefaultDeliveryNoteFromAddress: false,
  },
  {
    siteId: 4,
    code: "BUCKINGHAM02",
    description: "Buckingham - Display",
    isDefaultForPurchaseOrders: false,
    isDefaultDeliveryNoteFromAddress: false,
  },
];

// ── Cost centres ────────────────────────────────────────────────────────────
// The full 49-row CostCentres snapshot, real codes/sites/process types.

interface RawCostCentre {
  id: number;
  code: string;
  site: string;
  processTypeId: number;
}

const RAW_COST_CENTRES: RawCostCentre[] = [
  { id: 800, code: "PRE-PLATEMAKING-B2", site: "EXETER01", processTypeId: 2 },
  { id: 801, code: "FIN-GUILLOTINE", site: "EXETER01", processTypeId: 36 },
  { id: 802, code: "FIN-PRECUT", site: "EXETER01", processTypeId: 88 },
  { id: 803, code: "PNT-L1", site: "EXETER01", processTypeId: 53 },
  { id: 812, code: "FIN-FOLD-TI52", site: "EXETER01", processTypeId: 30 },
  { id: 813, code: "FIN-DIECUT01", site: "EXETER01", processTypeId: 96 },
  { id: 814, code: "FIN-LAM-AB-G", site: "EXETER01", processTypeId: 52 },
  { id: 815, code: "FIN-MATT-AB-G", site: "EXETER01", processTypeId: 52 },
  { id: 819, code: "PNT-L2", site: "EXETER01", processTypeId: 53 },
  { id: 820, code: "PRE-PLATEMAKING-B3", site: "EXETER01", processTypeId: 2 },
  { id: 824, code: "FIN-PERF-L3-02", site: "EXETER01", processTypeId: 66 },
  { id: 827, code: "FIN-COLL-FORMS", site: "EXETER01", processTypeId: 31 },
  { id: 828, code: "FIN-GLUPAD", site: "EXETER01", processTypeId: 33 },
  { id: 829, code: "FIN-WS", site: "EXETER01", processTypeId: 94 },
  { id: 830, code: "FIN-STITCH-01", site: "EXETER01", processTypeId: 82 },
  { id: 835, code: "FIN-FOLD-KH82", site: "EXETER01", processTypeId: 30 },
  {
    id: 843,
    code: "FIN-FOLDER-NON-CAPACITY-X1",
    site: "EXETER01",
    processTypeId: 39,
  },
  { id: 852, code: "FIN-PULLUPB", site: "EXETER01", processTypeId: 70 },
  {
    id: 882,
    code: "FIN-PACKING-BUS-CARDS",
    site: "EXETER01",
    processTypeId: 59,
  },
  {
    id: 883,
    code: "FIN-PACKING-BOXES-A4",
    site: "EXETER01",
    processTypeId: 59,
  },
  {
    id: 888,
    code: "FIN-PACKING-PADS-AND-BOOKS",
    site: "EXETER01",
    processTypeId: 59,
  },
  { id: 896, code: "PRE-PDF", site: "EXETER01", processTypeId: 64 },
  {
    id: 900,
    code: "FIN-HOHNER-STITCH-ONLY",
    site: "EXETER01",
    processTypeId: 50,
  },
  { id: 1034, code: "FIN-FOLD-TI52-2-UP", site: "EXETER01", processTypeId: 30 },
  { id: 1076, code: "FIN-COUNT-INSERT", site: "EXETER01", processTypeId: 19 },
  { id: 1079, code: "FIN-GLUESETS", site: "EXETER01", processTypeId: 35 },
  {
    id: 1084,
    code: "PNT-L2-FOR-NCR-SUBS-RUNS",
    site: "EXETER01",
    processTypeId: 53,
  },
  { id: 1117, code: "EX-PRE-VERSAFIRE1", site: "EXETER01", processTypeId: 24 },
  { id: 1137, code: "FIN-PRECUTBB", site: "EXETER01", processTypeId: 72 },
  { id: 4616, code: "PRECUT", site: "BUCKINGHAM01", processTypeId: 127 },
  { id: 4639, code: "DIECUTXY", site: "BUCKINGHAM01", processTypeId: 139 },
  {
    id: 4650,
    code: "PLATEMAKING SM52",
    site: "BUCKINGHAM01",
    processTypeId: 2,
  },
  { id: 4656, code: "2COLSM52", site: "BUCKINGHAM01", processTypeId: 53 },
  { id: 4842, code: "JBPDFPROOF", site: "BUCKINGHAM02", processTypeId: 187 },
  { id: 4867, code: "DIECUTXYREEL", site: "BUCKINGHAM01", processTypeId: 139 },
  { id: 4985, code: "DRILLINGPROD", site: "BUCKINGHAM01", processTypeId: 25 },
  { id: 5230, code: "PRECUT INDIGO", site: "BUCKINGHAM01", processTypeId: 127 },
  {
    id: 5274,
    code: "AMTMANN DUPLEX",
    site: "BUCKINGHAM01",
    processTypeId: 125,
  },
  { id: 5351, code: "5COLXL75LEUV", site: "EXETER01", processTypeId: 53 },
  {
    id: 5317,
    code: "HORIZONBQ470 DIGI",
    site: "EXETER01",
    processTypeId: 65,
  },
  {
    id: 5354,
    code: "5COLXL75LEUVNOGRIP LINEWORK",
    site: "EXETER01",
    processTypeId: 53,
  },
  { id: 5607, code: "HYBRID SHEET", site: "BUCKINGHAM01", processTypeId: 110 },
  {
    id: 5608,
    code: "HYBRID SHEET WHITE",
    site: "BUCKINGHAM01",
    processTypeId: 110,
  },
  { id: 5609, code: "HYBRID REEL", site: "BUCKINGHAM01", processTypeId: 91 },
  { id: 5611, code: "ROLLFED", site: "BUCKINGHAM01", processTypeId: 91 },
  { id: 5627, code: "HPINDIGO12000", site: "BUCKINGHAM01", processTypeId: 24 },
  {
    id: 5635,
    code: "ADDITIONALOVERS10",
    site: "BUCKINGHAM01",
    processTypeId: 6,
  },
  {
    id: 5729,
    code: "XEROX VERSANT 280",
    site: "BUCKINGHAM01",
    processTypeId: 24,
  },
  { id: 5826, code: "PACKA1DELTA", site: "BUCKINGHAM02", processTypeId: 59 },
  {
    id: 5830,
    code: "BU-PRE-RICOHC9500",
    site: "BUCKINGHAM01",
    processTypeId: 24,
  },
];

/** Decoded, plain-English names for the codes shown in more than a passing
 *  list row — Tharstern's own code is always shown alongside (naming.md:
 *  preserve the source term; decode is a documented, reviewed addition). */
const COST_CENTRE_NAMES: Record<number, string> = {
  803: "Litho printing line 1",
  5351: "5-colour XL75 LE-UV",
  5830: "Ricoh C9500 (Buckingham)",
  1117: "Versafire 1 (Exeter)",
  5627: "HP Indigo 12000",
  5729: "Xerox Versant 280",
  801: "Guillotine",
  830: "Saddle stitcher 1",
  835: "KH82 section folder",
  5274: "Amtmann Duplex folder",
  5607: "Hybrid sheet press",
  5609: "Hybrid reel press",
  4656: "2-colour SM52",
  813: "Die-cutter 01",
  883: "A4 box packing",
  5317: "Horizon BQ470 perfect binder",
};

function costCentreName(id: number, code: string): string {
  return COST_CENTRE_NAMES[id] ?? code;
}

const RAW_COST_CENTRES_BY_ID = new Map(
  RAW_COST_CENTRES.map((raw) => [raw.id, raw]),
);

/** Tharstern's own code and our decoded name for one cost centre — the
 *  single source other mock-data modules (`cost-engine-validation.ts`,
 *  `routing-policy.ts`) must look up rather than re-typing their own copy,
 *  so a cost centre never carries two different codes/names across pages
 *  (naming.md: one name per concept, in every layer). Throws on an unknown
 *  id rather than silently falling back — a validation/routing fixture
 *  citing a cost centre this snapshot doesn't have is a fixture bug, not a
 *  display gap. */
export function getMockCostCentreRef(costCentreId: number): {
  costCentreId: number;
  code: string;
  name: string;
  processTypeId: number;
} {
  const raw = RAW_COST_CENTRES_BY_ID.get(costCentreId);
  if (!raw) {
    throw new Error(
      `Unknown cost centre id ${costCentreId} — not in manufacturing-setup.ts's RAW_COST_CENTRES snapshot`,
    );
  }
  return {
    costCentreId: raw.id,
    code: raw.code,
    name: costCentreName(raw.id, raw.code),
    processTypeId: raw.processTypeId,
  };
}

const UNAVAILABLE_COST_CENTRES = new Set<number>([5729]);

/**
 * How many rolling-validation price checks have costed a line on this cost
 * centre — the setup detail page's one link-out line to rolling validation,
 * and the same figure the "never route to a press not yet used in any price
 * check" rule depends on. Kept in step with `mock-data/cost-engine-
 * validation.ts`'s `COST_LINE_FIXTURES` (each fixture's `totalCheckCount`,
 * summed per cost centre) so the two pages never disagree — decision 12
 * Sep, `03_manufacturing_surfaces_design.md` "setup visibility and costing
 * validation are separate": setup carries only the count, never the
 * cost-check breakdown behind it. Cost centres absent here have never been
 * used in a price check.
 */
const PRICE_CHECKS_USED_IN: Record<number, number> = {
  5351: 30,
  801: 22,
  5607: 0,
  5609: 0,
  5830: 96,
  1117: 64,
  5627: 30,
  835: 20,
  803: 58,
};

function priceChecksUsedInFor(id: number): number {
  return PRICE_CHECKS_USED_IN[id] ?? 0;
}

/** "Changed in Tharstern 10 Sep" — a pure setup fact from change tracking,
 *  with no validation meaning. Most cost centres have no recent change. */
const CHANGED_IN_THARSTERN: Record<number, string> = {
  803: "2026-09-10T14:05:00Z",
  5729: "2026-08-20T09:00:00Z",
  5351: "2026-09-09T14:10:00Z",
};

function changedInTharsternAtFor(id: number): string | null {
  return CHANGED_IN_THARSTERN[id] ?? null;
}

/** Rich sub-tab data for the 13 hero cost centres, built once and looked up
 *  by id. Every other cost centre in the snapshot gets a detail page with
 *  real header/gate data but empty sub-tabs — an honest "no rows captured
 *  for this cost centre" rather than fabricated numbers. */
interface HeroCostCentreData {
  rates: EngineUseField[];
  operations: EngineUseRow[];
  runSpeeds: EngineUseRow[];
  setups: EngineUseRow[];
  runs: EngineUseRow[];
  geometry: EngineUseRow[];
  packing: EngineUseRow[];
  inkSuitability: { inkProfileId: number; code: string; name: string }[];
}

function rateFields(
  chargeRatePence: number,
  labourRatePence: number,
  minChargePence: number,
  stations: number,
  maxInlineGsm: number | null,
): EngineUseField[] {
  return [
    field("rate", "price", "Charge rate", moneyPerHour(chargeRatePence)),
    field("rate", "labour", "Labour rate", moneyPerHour(labourRatePence)),
    field("rate", "min_charge", "Minimum charge", money(minChargePence)),
    field("cost_centre_gate", "stations", "Stations", String(stations)),
    field(
      "cost_centre_gate",
      "max_inline_gsm",
      "Max inline gsm",
      maxInlineGsm == null ? "—" : String(maxInlineGsm),
    ),
  ];
}

const INK_LOOKUP: Record<number, { code: string; name: string }> = {
  63: { code: "EX-7 COLOURS P7C", name: "EX-7 Pantone Colours/Coater" },
  94: { code: "BU-1PRO1METUV", name: "BU-1 Process/1 Metallic UV" },
  124: { code: "BU-3PROUV", name: "BU-3 Process Colours UV" },
  42: { code: "EX-4 COLOUR DIGITAL", name: "EX-4 Colour Digital" },
  128: { code: "BU-4PRO DIGITAL", name: "BU-4 Colour Process Digital" },
  166: { code: "EX-2 COLOURS COAT", name: "EX-2 Pantone Colour + Coater" },
  90: { code: "BU-SPECIAL1", name: "BU-Special1" },
  92: { code: "BU-1MET1SP", name: "BU-1 Metalic/1Spot" },
  69: { code: "BU-1MET", name: "BU-1 Metalic" },
  71: { code: "BU-4PRO VUTEK", name: "BU-4 Colour Process Vutek" },
  6: { code: "EX-1 COLOUR", name: "EX-1 Pantone Colour" },
  7: { code: "EX-1 COLOUR BLACK", name: "EX-1 Colour Black" },
};

function ink(id: number) {
  const entry = INK_LOOKUP[id];
  return { inkProfileId: id, code: entry.code, name: entry.name };
}

const HERO_COST_CENTRE_DATA: Record<number, HeroCostCentreData> = {
  // 803 · Litho printing line 1 (Exeter)
  803: {
    rates: rateFields(9945, 4845, 0, 5, null),
    operations: [
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "PNT-L1 M/R" },
          { label: "Charge rate", value: moneyPerHour(9945) },
          { label: "Labour rate", value: moneyPerHour(4845) },
        ],
      ),
      row(
        "rate",
        ["price", "labour", "timedb"],
        [
          { label: "Operation", value: "PNT-L1 RUN" },
          { label: "Charge rate", value: moneyPerHour(9945) },
          { label: "Labour rate", value: moneyPerHour(4845) },
        ],
      ),
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "PNT-L1 WASH UP" },
          { label: "Charge rate", value: moneyPerHour(9945) },
          { label: "Labour rate", value: moneyPerHour(4845) },
        ],
      ),
    ],
    runSpeeds: [
      row(
        "run_speed",
        ["weight", "run_speed"],
        [
          { label: "Weight (up to)", value: "250 gsm" },
          { label: "Run speed", value: "12,500 sph" },
        ],
      ),
      row(
        "run_speed",
        ["weight", "run_speed"],
        [
          { label: "Weight (up to)", value: "450 gsm" },
          { label: "Run speed", value: "10,000 sph" },
        ],
      ),
      row(
        "run_speed",
        ["weight", "run_speed"],
        [
          { label: "Weight (up to)", value: "600 gsm" },
          { label: "Run speed", value: "2,500 sph" },
        ],
      ),
    ],
    setups: [
      row(
        "setup",
        ["setup_type", "quantity_factor", "duration"],
        [
          { label: "Setup type", value: "Make-ready (1 plate)" },
          { label: "Duration", value: "3 min" },
        ],
      ),
      row(
        "setup",
        ["setup_type", "quantity_factor", "duration", "quantity_spoils"],
        [
          { label: "Setup type", value: "Plates 2–5" },
          { label: "Duration", value: "6–15 min" },
          { label: "Spoils", value: "150–300 sheets" },
        ],
      ),
      row(
        "setup",
        ["setup_type", "quantity_spoils"],
        [
          { label: "Setup type", value: "Wash-up" },
          { label: "Spoils", value: "999 sheets" },
        ],
      ),
    ],
    runs: [],
    geometry: [
      row(
        "geometry",
        ["print_method", "grip_left", "grip_right", "back_edge", "grip_edge"],
        [
          { label: "Print method", value: "Sheet" },
          { label: "Grip L/R", value: "5 / 5 mm" },
          { label: "Back edge / grip edge", value: "10 / 10 mm" },
        ],
      ),
    ],
    packing: [],
    inkSuitability: [ink(63), ink(6), ink(7)],
  },
  // 5351 · 5-colour XL75 LE-UV (Exeter) — the design note's own worked example.
  5351: {
    rates: rateFields(9945, 4845, 500, 5, null),
    operations: [
      row(
        "rate",
        ["price", "labour", "timedb"],
        [
          { label: "Operation", value: "5COLXL75LEUV RUN" },
          { label: "Charge rate", value: moneyPerHour(9945) },
          { label: "Labour rate", value: moneyPerHour(4845) },
        ],
      ),
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "5COLXL75LEUV WASH UP" },
          { label: "Charge rate", value: moneyPerHour(9945) },
          { label: "Labour rate", value: moneyPerHour(4845) },
        ],
      ),
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "5COLXL75LEUV M/R" },
          { label: "Charge rate", value: moneyPerHour(9945) },
          { label: "Labour rate", value: moneyPerHour(4845) },
        ],
      ),
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "5COLXL75LEUV FOLEX CHANGE" },
          { label: "Charge rate", value: moneyPerHour(9945) },
          { label: "Labour rate", value: moneyPerHour(4845) },
        ],
      ),
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "5COLXL75LEUV TURNING SHEETS" },
          { label: "Charge rate", value: moneyPerHour(9945) },
          { label: "Labour rate", value: moneyPerHour(4845) },
        ],
      ),
    ],
    runSpeeds: [
      row(
        "run_speed",
        ["weight", "run_speed"],
        [
          { label: "Weight (up to)", value: "250 gsm" },
          { label: "Run speed", value: "12,500 sph" },
        ],
      ),
      row(
        "run_speed",
        ["weight", "run_speed"],
        [
          { label: "Weight (up to)", value: "450 gsm" },
          { label: "Run speed", value: "10,000 sph" },
        ],
      ),
      row(
        "run_speed",
        ["weight", "run_speed"],
        [
          { label: "Weight (up to)", value: "600 gsm" },
          { label: "Run speed", value: "2,500 sph" },
        ],
      ),
    ],
    setups: [
      row(
        "setup",
        ["setup_type", "quantity_factor"],
        [
          { label: "Setup type", value: "Make-ready (5 plates)" },
          { label: "Duration", value: "instant" },
        ],
      ),
      row(
        "setup",
        ["setup_type", "quantity_factor", "duration", "quantity_spoils"],
        [
          { label: "Setup type", value: "Plates 2–5" },
          { label: "Duration", value: "6–15 min" },
          { label: "Spoils", value: "150–300 sheets" },
        ],
      ),
    ],
    runs: [],
    geometry: [
      row(
        "geometry",
        [
          "print_method",
          "grip_left",
          "grip_right",
          "back_edge",
          "grip_edge",
          "colour_bar",
        ],
        [
          { label: "Print method", value: "Sheet" },
          { label: "Grip L/R", value: "5 / 5 mm" },
          { label: "Back edge / grip edge", value: "2 / 8 mm" },
          { label: "Colour bar", value: "16 mm" },
        ],
      ),
    ],
    packing: [],
    inkSuitability: [ink(94), ink(124)],
  },
  // 5830 · Ricoh C9500 (Buckingham)
  5830: {
    rates: rateFields(7800, 3800, 0, 5, null),
    operations: [
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "RICOHC9500 MR" },
          { label: "Charge rate", value: moneyPerHour(6500) },
          { label: "Labour rate", value: moneyPerHour(2800) },
        ],
      ),
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "RICOHC9500 RUN" },
          { label: "Charge rate", value: moneyPerHour(7800) },
          { label: "Labour rate", value: moneyPerHour(3800) },
        ],
      ),
      row(
        "rate",
        ["price"],
        [
          { label: "Operation", value: "RICOHC9500 COLOUR CLICK" },
          { label: "Charge rate", value: money(3850) },
        ],
      ),
      row(
        "rate",
        ["price"],
        [
          { label: "Operation", value: "RICOHC9500 MONO CLICK" },
          { label: "Charge rate", value: money(1250) },
        ],
      ),
    ],
    runSpeeds: [
      row(
        "run_speed",
        ["weight", "run_speed", "spoils_percentage"],
        [
          { label: "Weight (up to)", value: "400 gsm" },
          { label: "Run speed", value: "2,500 sph" },
          { label: "Spoils", value: "5%" },
        ],
      ),
      row(
        "run_speed",
        ["weight", "run_speed", "spoils_percentage"],
        [
          { label: "Weight (up to)", value: "510 gsm" },
          { label: "Run speed", value: "2,500 sph" },
          { label: "Spoils", value: "5%" },
        ],
      ),
    ],
    setups: [
      row(
        "setup",
        ["setup_type", "quantity_factor", "duration", "quantity_spoils"],
        [
          { label: "Setup type", value: "Make-ready" },
          { label: "Duration", value: "10 min" },
          { label: "Spoils", value: "15 sheets" },
        ],
      ),
    ],
    runs: [],
    geometry: [
      row(
        "geometry",
        ["grip_left", "grip_right", "back_edge", "grip_edge"],
        [
          { label: "Type", value: "Digital sheet" },
          { label: "Grip L/R", value: "5 / 5 mm" },
          { label: "Back edge / grip edge", value: "5 / 5 mm" },
        ],
      ),
    ],
    packing: [],
    inkSuitability: [ink(42)],
  },
  // 1117 · Versafire 1 (Exeter)
  1117: {
    rates: rateFields(8160, 4080, 0, 5, null),
    operations: [
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "EX-PNT-VERSAFIRE MR" },
          { label: "Charge rate", value: moneyPerHour(7140) },
          { label: "Labour rate", value: moneyPerHour(3060) },
        ],
      ),
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "EX-PNT-VERSAFIRE RUN" },
          { label: "Charge rate", value: moneyPerHour(8160) },
          { label: "Labour rate", value: moneyPerHour(4080) },
        ],
      ),
      row(
        "rate",
        ["price"],
        [
          { label: "Operation", value: "EX-PNT-VERSAFIRE COLOUR CLICK" },
          { label: "Charge rate", value: money(4080) },
        ],
      ),
      row(
        "rate",
        ["price"],
        [
          { label: "Operation", value: "EX-PNT-VERSAFIRE MONO CLICK" },
          { label: "Charge rate", value: money(4080) },
        ],
      ),
    ],
    runSpeeds: [
      row(
        "run_speed",
        ["weight", "run_speed", "spoils_percentage"],
        [
          { label: "Weight (up to)", value: "400 gsm" },
          { label: "Run speed", value: "2,500 sph" },
          { label: "Spoils", value: "5%" },
        ],
      ),
      row(
        "run_speed",
        ["weight", "run_speed", "spoils_percentage"],
        [
          { label: "Weight (up to)", value: "450 gsm, SRA3" },
          { label: "Run speed", value: "2,500 sph" },
          { label: "Spoils", value: "5%" },
        ],
      ),
    ],
    setups: [
      row(
        "setup",
        ["setup_type", "quantity_factor", "duration", "quantity_spoils"],
        [
          { label: "Setup type", value: "Make-ready" },
          { label: "Duration", value: "10 min" },
          { label: "Spoils", value: "15 sheets" },
        ],
      ),
    ],
    runs: [],
    geometry: [
      row(
        "geometry",
        ["type", "grip_left", "grip_right", "back_edge", "grip_edge"],
        [
          { label: "Type", value: "Digital sheet 1" },
          { label: "Grip L/R", value: "5 / 5 mm" },
          { label: "Back edge / grip edge", value: "5 / 5 mm" },
        ],
      ),
      row(
        "geometry",
        ["type", "grip_left", "grip_right", "back_edge", "grip_edge"],
        [
          { label: "Type", value: "Digital sheet 2" },
          { label: "Grip L/R", value: "5 / 5 mm" },
          { label: "Back edge / grip edge", value: "5 / 5 mm" },
        ],
      ),
    ],
    packing: [],
    inkSuitability: [ink(42)],
  },
  // 5627 · HP Indigo 12000 (Buckingham)
  5627: {
    rates: rateFields(14500, 5200, 0, 7, null),
    operations: [
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "HPINDIGO12000 MR" },
          { label: "Charge rate", value: moneyPerHour(9500) },
          { label: "Labour rate", value: moneyPerHour(4000) },
        ],
      ),
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "HPINDIGO12000 RUN" },
          { label: "Charge rate", value: moneyPerHour(14500) },
          { label: "Labour rate", value: moneyPerHour(5200) },
        ],
      ),
      row(
        "rate",
        ["price"],
        [
          { label: "Operation", value: "HPINDIGO12000 COLOUR CLICK" },
          { label: "Charge rate", value: money(4200) },
        ],
      ),
    ],
    runSpeeds: [
      row(
        "run_speed",
        ["weight", "run_speed", "spoils_percentage"],
        [
          { label: "Weight (up to)", value: "400 gsm" },
          { label: "Run speed", value: "3,105 sph" },
          { label: "Spoils", value: "5%" },
        ],
      ),
      row(
        "run_speed",
        ["weight", "run_speed", "spoils_percentage"],
        [
          { label: "Weight (up to)", value: "450 gsm" },
          { label: "Run speed", value: "3,105 sph" },
          { label: "Spoils", value: "5%" },
        ],
      ),
    ],
    setups: [
      row(
        "setup",
        ["setup_type", "quantity_factor", "duration", "quantity_spoils"],
        [
          { label: "Setup type", value: "Make-ready" },
          { label: "Duration", value: "5 min" },
          { label: "Spoils", value: "15–50 sheets" },
        ],
      ),
    ],
    runs: [],
    geometry: [
      row(
        "geometry",
        ["grip_left", "grip_right", "back_edge", "grip_edge"],
        [
          { label: "Type", value: "Digital sheet 1" },
          { label: "Grip L/R", value: "2 / 2 mm" },
          { label: "Back edge / grip edge", value: "7 / 10 mm" },
        ],
      ),
    ],
    packing: [],
    inkSuitability: [ink(128)],
  },
  // 5729 · Xerox Versant 280 (Buckingham) — unavailable, never checked.
  5729: {
    rates: rateFields(6800, 3200, 0, 4, null),
    operations: [
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "VERSANT280 MR" },
          { label: "Charge rate", value: moneyPerHour(5500) },
          { label: "Labour rate", value: moneyPerHour(2400) },
        ],
      ),
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "VERSANT280 RUN" },
          { label: "Charge rate", value: moneyPerHour(6800) },
          { label: "Labour rate", value: moneyPerHour(3200) },
        ],
      ),
      row(
        "rate",
        ["price"],
        [
          { label: "Operation", value: "VERSANT280 CLICK" },
          { label: "Charge rate", value: money(2800) },
        ],
      ),
    ],
    runSpeeds: [
      row(
        "run_speed",
        ["weight", "run_speed", "spoils_percentage"],
        [
          { label: "Weight (up to)", value: "400 gsm" },
          { label: "Run speed", value: "1,550 sph" },
          { label: "Spoils", value: "5%" },
        ],
      ),
    ],
    setups: [
      row(
        "setup",
        ["setup_type", "quantity_factor", "duration", "quantity_spoils"],
        [
          { label: "Setup type", value: "Make-ready" },
          { label: "Duration", value: "5 min" },
          { label: "Spoils", value: "15 sheets" },
        ],
      ),
    ],
    runs: [],
    geometry: [
      row(
        "geometry",
        ["grip_left", "grip_right", "back_edge", "grip_edge"],
        [
          { label: "Type", value: "Digital sheet 1" },
          { label: "Grip L/R", value: "2 / 2 mm" },
          { label: "Back edge / grip edge", value: "2 / 2 mm" },
        ],
      ),
    ],
    packing: [],
    inkSuitability: [ink(128)],
  },
  // 801 · Guillotine (Exeter)
  801: {
    rates: rateFields(8364, 3264, 300, 0, 0),
    operations: [
      row(
        "rate",
        ["price", "labour", "min_charge", "run_speed"],
        [
          { label: "Operation", value: "EX-FIN-GUILLOTINE RUN" },
          { label: "Charge rate", value: moneyPerHour(8364) },
          { label: "Labour rate", value: moneyPerHour(3264) },
          { label: "Min charge", value: money(300) },
        ],
      ),
      row(
        "rate",
        ["price", "labour", "min_charge", "run_speed"],
        [
          { label: "Operation", value: "EX-FIN-POLAR GUILLOTINE RUN" },
          { label: "Charge rate", value: moneyPerHour(8364) },
          { label: "Labour rate", value: moneyPerHour(3264) },
          { label: "Min charge", value: money(300) },
        ],
      ),
    ],
    runSpeeds: [],
    setups: [],
    runs: [
      row(
        "run",
        ["quantity", "weight", "run_speed", "duration"],
        [
          { label: "Quantity", value: "70 cuts" },
          { label: "Run speed", value: "85/min" },
          { label: "Duration", value: "1,060 min" },
        ],
      ),
    ],
    geometry: [],
    packing: [],
    inkSuitability: [],
  },
  // 835 · KH82 section folder (Exeter)
  835: {
    rates: rateFields(8364, 3264, 2000, 0, 0),
    operations: [
      row(
        "rate",
        ["price", "labour", "min_charge", "timedb"],
        [
          { label: "Operation", value: "EX-FIN-SECTION FOLDER KH82 M/R" },
          { label: "Charge rate", value: moneyPerHour(8364) },
          { label: "Labour rate", value: moneyPerHour(3264) },
          { label: "Min charge", value: money(2000) },
        ],
      ),
    ],
    runSpeeds: [
      row(
        "run_speed",
        ["weight", "run_speed", "fold_type"],
        [
          { label: "Weight", value: "170 gsm" },
          { label: "Speed multiplier (1–6 folds)", value: "1.0 → 0.6" },
        ],
      ),
      row(
        "run_speed",
        ["weight", "run_speed", "fold_type"],
        [
          { label: "Weight", value: "250 gsm" },
          { label: "Speed multiplier (1–3 folds)", value: "0.86 → 0.79" },
        ],
      ),
      row(
        "run_speed",
        ["weight", "run_speed", "fold_type"],
        [
          { label: "Weight", value: "600 gsm" },
          { label: "Speed multiplier (1–3 folds)", value: "0.79 → 0.71" },
        ],
      ),
    ],
    setups: [
      row(
        "setup",
        ["setup_type", "quantity_factor", "duration", "fold_type"],
        [
          { label: "Setup type", value: "Fold 1–6, letter fold" },
          { label: "Duration", value: "30–50 min" },
        ],
      ),
      row(
        "setup",
        ["setup_type", "quantity_factor", "duration"],
        [
          { label: "Setup type", value: "Fold 1–2, no fold-type" },
          { label: "Duration", value: "5–10 min" },
        ],
      ),
    ],
    runs: [],
    geometry: [],
    packing: [],
    inkSuitability: [],
  },
  // 5274 · Amtmann Duplex folder (Buckingham)
  5274: {
    rates: rateFields(7100, 2800, 0, 0, 0),
    operations: [
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "AMTMANN DUPLEX M/R" },
          { label: "Charge rate", value: moneyPerHour(7100) },
          { label: "Labour rate", value: moneyPerHour(2800) },
        ],
      ),
    ],
    runSpeeds: [
      row(
        "run_speed",
        ["weight", "run_speed"],
        [
          { label: "Weight (up to)", value: "2,000 gsm" },
          { label: "Run speed", value: "115 sph" },
        ],
      ),
      row(
        "run_speed",
        ["weight", "run_speed"],
        [
          { label: "Weight (up to)", value: "2,000 gsm" },
          { label: "Run speed", value: "180 sph" },
        ],
      ),
    ],
    setups: [
      row(
        "setup",
        ["setup_type", "duration", "quantity_spoils"],
        [
          { label: "Setup type", value: "Make-ready" },
          { label: "Duration", value: "30 min" },
          { label: "Spoils", value: "2 sheets" },
        ],
      ),
    ],
    runs: [],
    geometry: [
      row("geometry", ["type"], [{ label: "Type", value: "Duplex feed" }]),
    ],
    packing: [],
    inkSuitability: [],
  },
  // 5607 · Hybrid sheet press (Buckingham) — the brief's designed-in grey
  // state: RUN prices from a value only Tharstern computes.
  5607: {
    rates: rateFields(12000, 4500, 0, 5, 50000),
    operations: [
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "HYBRID SHEET RUN" },
          { label: "Charge rate", value: moneyPerHour(12000) },
          { label: "Labour rate", value: moneyPerHour(4500) },
        ],
      ),
    ],
    runSpeeds: [
      row(
        "run_speed",
        ["weight", "run_speed", "spoils_percentage"],
        [
          { label: "Weight (up to)", value: "any" },
          { label: "Run speed", value: "30 sph" },
        ],
      ),
    ],
    setups: [
      row(
        "setup",
        ["setup_type", "quantity_factor", "duration"],
        [
          { label: "Setup type", value: "Make-ready" },
          { label: "Duration", value: "5 min" },
        ],
      ),
      row(
        "setup",
        ["setup_type", "quantity_factor", "duration"],
        [
          { label: "Setup type", value: "Ink change" },
          { label: "Duration", value: "0.5 min" },
        ],
      ),
    ],
    runs: [],
    geometry: [
      row(
        "geometry",
        ["type"],
        [{ label: "Type", value: "Wide-format sheet 1" }],
      ),
      row(
        "geometry",
        ["type"],
        [{ label: "Type", value: "Wide-format sheet 2" }],
      ),
    ],
    packing: [],
    inkSuitability: [ink(71)],
  },
  // 4656 · 2-colour SM52 (Buckingham)
  4656: {
    rates: rateFields(6200, 2800, 0, 2, 550),
    operations: [
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "2COLSM52 M/R" },
          { label: "Charge rate", value: moneyPerHour(5500) },
          { label: "Labour rate", value: moneyPerHour(2400) },
        ],
      ),
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "2COLSM52 RUN" },
          { label: "Charge rate", value: moneyPerHour(6200) },
          { label: "Labour rate", value: moneyPerHour(2800) },
        ],
      ),
    ],
    runSpeeds: [
      row(
        "run_speed",
        ["weight", "run_speed", "spoils_percentage"],
        [
          { label: "Weight (up to)", value: "115 gsm" },
          { label: "Run speed", value: "5,500–7,000 sph" },
          { label: "Spoils", value: "1–2%" },
        ],
      ),
      row(
        "run_speed",
        ["weight", "run_speed", "spoils_percentage"],
        [
          { label: "Weight (up to)", value: "170 gsm" },
          { label: "Run speed", value: "5,000–6,500 sph" },
          { label: "Spoils", value: "1–2%" },
        ],
      ),
      row(
        "run_speed",
        ["weight", "run_speed", "spoils_percentage"],
        [
          { label: "Weight (up to)", value: "300 gsm" },
          { label: "Run speed", value: "4,500–6,000 sph" },
          { label: "Spoils", value: "1–2%" },
        ],
      ),
      row(
        "run_speed",
        ["weight", "run_speed", "spoils_percentage"],
        [
          { label: "Weight (up to)", value: "400 gsm" },
          { label: "Run speed", value: "2,000–2,750 sph" },
          { label: "Spoils", value: "1.5–2%" },
        ],
      ),
    ],
    setups: [
      row(
        "setup",
        ["setup_type", "quantity_factor", "duration", "quantity_spoils"],
        [
          { label: "Setup type", value: "Plate 1–2" },
          { label: "Duration", value: "10–20 min" },
          { label: "Spoils", value: "100–200 sheets" },
        ],
      ),
    ],
    runs: [],
    geometry: [
      row(
        "geometry",
        ["print_method", "grip_left", "grip_right", "back_edge", "grip_edge"],
        [
          { label: "Print method", value: "Sheet" },
          { label: "Grip L/R", value: "5 / 5 mm" },
          { label: "Back edge / grip edge", value: "5 / 10 mm" },
        ],
      ),
    ],
    packing: [],
    inkSuitability: [ink(90), ink(92), ink(69)],
  },
  // 813 · Die-cutter 01 (Exeter)
  813: {
    rates: rateFields(6936, 3264, 300, 0, 0),
    operations: [
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "DIECUT01 M/R" },
          { label: "Charge rate", value: moneyPerHour(5916) },
          { label: "Labour rate", value: moneyPerHour(2244) },
        ],
      ),
      row(
        "rate",
        ["price", "labour"],
        [
          { label: "Operation", value: "DIECUT01 RUN" },
          { label: "Charge rate", value: moneyPerHour(6936) },
          { label: "Labour rate", value: moneyPerHour(3264) },
        ],
      ),
    ],
    runSpeeds: [
      row(
        "run_speed",
        ["weight", "run_speed", "duration_band"],
        [
          { label: "Weight", value: "450 gsm" },
          { label: "Run speed", value: "1,500–2,100 sph" },
        ],
      ),
    ],
    setups: [
      row(
        "setup",
        ["setup_type", "duration"],
        [
          { label: "Setup type", value: "Make-ready" },
          { label: "Duration", value: "30 min" },
        ],
      ),
    ],
    runs: [],
    geometry: [],
    packing: [],
    inkSuitability: [],
  },
  // 883 · A4 box packing (Exeter)
  883: {
    rates: rateFields(0, 0, 300, 0, 0),
    operations: [],
    runSpeeds: [],
    setups: [],
    runs: [],
    geometry: [],
    packing: [
      row(
        "packing",
        [
          "box_quantity",
          "box_units_type",
          "finished_size_code_from",
          "finished_size_code_to",
        ],
        [
          { label: "Box", value: "Business Card Boxes" },
          { label: "Box quantity", value: "100 per box" },
          { label: "Size range", value: "1×1 → 65×95" },
        ],
      ),
      row(
        "packing",
        [
          "box_quantity",
          "box_units_type",
          "finished_size_code_from",
          "finished_size_code_to",
        ],
        [
          { label: "Box", value: "01 Packing A4 Boxes" },
          { label: "Box quantity", value: "10 per box" },
          { label: "Size range", value: "1×1 → A0" },
        ],
      ),
    ],
    inkSuitability: [],
  },
};

const COST_CENTRE_ENGINE_TOTALS = { used: 5, total: 7 };

function costCentrePriceChecksUrl(costCentreId: number): string {
  return `/views/live-costing/cost-centres?cost_centre_id=${costCentreId}`;
}

function costCentreIndexEntry(raw: RawCostCentre): CostCentreIndexEntry {
  return {
    costCentreId: raw.id,
    _self: `/views/manufacturing/cost-centres/${raw.id}`,
    type: "cost_centre",
    priceChecksUrl: costCentrePriceChecksUrl(raw.id),
    code: raw.code,
    name: costCentreName(raw.id, raw.code),
    processTypeId: raw.processTypeId,
    processTypeName:
      PROCESS_TYPE_NAMES.get(raw.processTypeId) ??
      `Process type ${raw.processTypeId}`,
    site: raw.site,
    isAvailable: !UNAVAILABLE_COST_CENTRES.has(raw.id),
    changedInTharsternAt: changedInTharsternAtFor(raw.id),
    engineUseUsedCount: HERO_COST_CENTRE_DATA[raw.id]
      ? COST_CENTRE_ENGINE_TOTALS.used
      : 0,
    engineUseTotalCount: HERO_COST_CENTRE_DATA[raw.id]
      ? COST_CENTRE_ENGINE_TOTALS.total
      : 0,
  };
}

const COST_CENTRE_SORT_FNS: Record<
  string,
  (e: CostCentreIndexEntry) => string | number
> = {
  code: (e) => e.code,
  name: (e) => e.name,
  site: (e) => e.site,
  costCentreId: (e) => e.costCentreId,
};

export function listMockManufacturingCostCentres(
  options?: CostCentreListOptions,
): ListResponse<CostCentreIndexEntry> {
  let entries = RAW_COST_CENTRES.map(costCentreIndexEntry);
  if (options?.q) {
    const needle = options.q.toLowerCase();
    entries = entries.filter(
      (e) =>
        e.code.toLowerCase().includes(needle) ||
        e.name.toLowerCase().includes(needle),
    );
  }
  if (options?.processTypeId != null) {
    entries = entries.filter((e) => e.processTypeId === options.processTypeId);
  }
  if (options?.site) {
    entries = entries.filter((e) => e.site === options.site);
  }
  if (options?.availability) {
    entries = entries.filter((e) =>
      options.availability === "available" ? e.isAvailable : !e.isAvailable,
    );
  }
  entries = sortBy(entries, options?.sort, COST_CENTRE_SORT_FNS, "code");
  return paginate(entries, options);
}

export function getMockManufacturingCostCentre(
  costCentreId: number,
): CostCentreDetail | null {
  const raw = RAW_COST_CENTRES.find((c) => c.id === costCentreId);
  if (!raw) return null;
  const hero = HERO_COST_CENTRE_DATA[costCentreId];
  return {
    costCentreId: raw.id,
    _self: `/views/manufacturing/cost-centres/${raw.id}`,
    type: "cost_centre",
    priceChecksUrl: costCentrePriceChecksUrl(raw.id),
    code: raw.code,
    name: costCentreName(raw.id, raw.code),
    processTypeId: raw.processTypeId,
    processTypeName:
      PROCESS_TYPE_NAMES.get(raw.processTypeId) ??
      `Process type ${raw.processTypeId}`,
    site: raw.site,
    isAvailable: !UNAVAILABLE_COST_CENTRES.has(raw.id),
    changedInTharsternAt: changedInTharsternAtFor(raw.id),
    priceChecksUsedIn: priceChecksUsedInFor(raw.id),
    sync: SYNC.costCentres,
    rates: hero?.rates ?? [],
    operations: hero?.operations ?? [],
    runSpeeds: hero?.runSpeeds ?? [],
    setups: hero?.setups ?? [],
    runs: hero?.runs ?? [],
    geometry: hero?.geometry ?? [],
    packing: hero?.packing ?? [],
    inkSuitability: hero?.inkSuitability ?? [],
  };
}

// ── Ink profiles ────────────────────────────────────────────────────────────

interface RawInkProfile {
  id: number;
  code: string;
  name: string;
  isLegacy: boolean;
  processInks: number;
  spotInks: number;
  metallicInks: number;
  coatings: number;
  suitableOn: number[];
}

const RAW_INK_PROFILES: RawInkProfile[] = [
  {
    id: 63,
    code: "EX-7 COLOURS P7C",
    name: "EX-7 Pantone Colours/Coater",
    isLegacy: false,
    processInks: 0,
    spotInks: 7,
    metallicInks: 0,
    coatings: 1,
    suitableOn: [803],
  },
  {
    id: 94,
    code: "BU-1PRO1METUV",
    name: "BU-1 Process/1 Metallic UV",
    isLegacy: false,
    processInks: 1,
    spotInks: 0,
    metallicInks: 1,
    coatings: 0,
    suitableOn: [5351],
  },
  {
    id: 124,
    code: "BU-3PROUV",
    name: "BU-3 Process Colours UV",
    isLegacy: false,
    processInks: 3,
    spotInks: 0,
    metallicInks: 0,
    coatings: 0,
    suitableOn: [5351],
  },
  {
    id: 42,
    code: "EX-4 COLOUR DIGITAL",
    name: "EX-4 Colour Digital",
    isLegacy: false,
    processInks: 4,
    spotInks: 0,
    metallicInks: 0,
    coatings: 0,
    suitableOn: [1117, 5830],
  },
  {
    id: 128,
    code: "BU-4PRO DIGITAL",
    name: "BU-4 Colour Process Digital",
    isLegacy: false,
    processInks: 4,
    spotInks: 0,
    metallicInks: 0,
    coatings: 0,
    suitableOn: [5627, 5729],
  },
  {
    id: 166,
    code: "EX-2 COLOURS COAT",
    name: "EX-2 Pantone Colour + Coater",
    isLegacy: true,
    processInks: 0,
    spotInks: 0,
    metallicInks: 0,
    coatings: 0,
    suitableOn: [803],
  },
];

function inkProfileIndexEntry(raw: RawInkProfile): InkProfileIndexEntry {
  return {
    inkProfileId: raw.id,
    _self: `/views/manufacturing/ink-profiles/${raw.id}`,
    type: "ink_profile",
    code: raw.code,
    name: raw.name,
    isLegacy: raw.isLegacy,
    processInks: raw.processInks,
    spotInks: raw.spotInks,
    metallicInks: raw.metallicInks,
    coatings: raw.coatings,
    suitablePressCount: raw.suitableOn.length,
  };
}

const INK_PROFILE_SORT_FNS: Record<
  string,
  (e: InkProfileIndexEntry) => string | number
> = {
  code: (e) => e.code,
  name: (e) => e.name,
};

export function listMockManufacturingInkProfiles(
  options?: InkProfileListOptions,
): ListResponse<InkProfileIndexEntry> {
  let entries = RAW_INK_PROFILES.map(inkProfileIndexEntry);
  if (options?.q) {
    const needle = options.q.toLowerCase();
    entries = entries.filter(
      (e) =>
        e.code.toLowerCase().includes(needle) ||
        e.name.toLowerCase().includes(needle),
    );
  }
  if (options?.isLegacy != null) {
    entries = entries.filter((e) => e.isLegacy === options.isLegacy);
  }
  entries = sortBy(entries, options?.sort, INK_PROFILE_SORT_FNS, "code");
  return paginate(entries, options);
}

export function getMockManufacturingInkProfile(
  inkProfileId: number,
): InkProfileDetail | null {
  const raw = RAW_INK_PROFILES.find((p) => p.id === inkProfileId);
  if (!raw) return null;
  return {
    ...inkProfileIndexEntry(raw),
    sync: SYNC.inkProfiles,
    fields: [
      field(
        "ink_profile",
        "quantity",
        "Ink slots",
        String(raw.processInks + raw.spotInks + raw.metallicInks),
      ),
      field(
        "ink_profile",
        "process_inks",
        "Process inks",
        String(raw.processInks),
      ),
      field("ink_profile", "spot_inks", "Spot inks", String(raw.spotInks)),
      field(
        "ink_profile",
        "metallic_inks",
        "Metallic inks",
        String(raw.metallicInks),
      ),
      field("ink_profile", "coatings", "Coatings", String(raw.coatings)),
      field(
        "ink_profile",
        "is_legacy",
        "Legacy profile",
        raw.isLegacy ? "Yes" : "No",
      ),
    ],
    suitableOn: raw.suitableOn.map((id) => {
      const cc = RAW_COST_CENTRES.find((c) => c.id === id)!;
      return {
        costCentreId: cc.id,
        code: cc.code,
        name: costCentreName(cc.id, cc.code),
        site: cc.site,
      };
    }),
    engineConstants: [
      {
        key: "ink_price_per_kg",
        label: "Ink price",
        value: "£8.40/kg (family constant)",
        status: "not_yet_replicated",
        why: "The real per-colour Inks/InkProfileDetail join isn't replicated yet — the engine uses one hard-coded constant across the whole profile family instead of a per-profile standard.",
      },
      {
        key: "ink_coverage",
        label: "Coverage assumption",
        value: "12% per printed colour",
        status: "not_yet_replicated",
        why: "Same gap — a per-profile coverage standard would replace this family-wide constant.",
      },
      {
        key: "ink_density",
        label: "Density",
        value: "1.0 g/cm³",
        status: "not_yet_replicated",
        why: "Same gap — density varies by ink type in reality; the engine assumes one value.",
      },
    ],
  };
}

// ── Product types ───────────────────────────────────────────────────────────

function pressCandidates(
  ids: number[],
): { costCentreId: number; code: string }[] {
  return ids.map((id) => {
    const cc = RAW_COST_CENTRES.find((c) => c.id === id);
    return { costCentreId: id, code: cc?.code ?? String(id) };
  });
}

function pressConsidered(
  code: number,
  outcome: ManufacturingPressConsidered["outcome"],
  reason: string,
): ManufacturingPressConsidered {
  const cc = RAW_COST_CENTRES.find((c) => c.id === code)!;
  return {
    pressCode: cc.code,
    pressName: costCentreName(cc.id, cc.code),
    site: cc.site,
    outcome,
    reason,
  };
}

/** How many rolling-validation price checks have priced an item on this
 *  product type — the detail page's one link-out line to rolling
 *  validation's by-product-type view. Kept in step with `mock-data/
 *  cost-engine-validation.ts`'s own dictionary-item fixtures (same rule as
 *  `PRICE_CHECKS_USED_IN` above: setup carries only the count, never the
 *  check breakdown behind it) — PT44's wide-format-panels fixture has never
 *  returned a check, so it is honestly 0, same as its presses. */
const PRODUCT_TYPE_PRICE_CHECKS: Record<number, number> = {
  1: 6, // Brochure Cover & Text — 2 variants × 3 quantities
  36: 6, // Perfect Bound Book
  44: 0, // Wide format panels — never checked
  63: 6, // Business cards
  77: 6, // Letterheads
  91: 90, // Greeting cards — 45 variants × 2 quantities
};

function productTypePriceChecksUsedIn(productTypeId: number): number {
  return PRODUCT_TYPE_PRICE_CHECKS[productTypeId] ?? 0;
}

const PRODUCT_TYPES: ProductTypeDetail[] = [
  {
    productTypeId: 1,
    _self: "/views/manufacturing/product-types/1",
    type: "product_type",
    priceChecksUrl: "/views/live-costing/product-types?product_type_id=1",
    priceChecksUsedIn: productTypePriceChecksUsedIn(1),
    name: "Brochure Cover & Text",
    isReplicated: true,
    notReplicatedReason: null,
    orderExpensePence: 750,
    markupMinPct: null,
    markupMaxPct: null,
    sync: SYNC.productTypes,
    parts: [
      {
        part: {
          productTypePartId: 1,
          partName: "Cover",
          isNoPrinting: false,
          defaultOrientation: "—",
          fields: [
            field(
              "product_type_part",
              "is_no_printing",
              "No printing",
              // isNoPrinting is false for every part in this snapshot — say
              // so in words, not as a bare "No" next to a "No printing"
              // label (a double negative Henry would have to puzzle out).
              "Printed normally",
            ),
            field(
              "product_type_part",
              "default_printing_cost_centre_id",
              "Default printing cost centre",
              "5351",
            ),
            field(
              "product_type_part",
              "default_orientation",
              "Default orientation",
              "—",
            ),
          ],
        },
        stepMap: [
          {
            lineNumber: 1,
            processTypeId: 53,
            processTypeName: "Litho Printing",
            isRequired: false,
            isDefault: false,
            defaultCostCentre: { costCentreId: 5351, code: "5COLXL75LEUV" },
            candidateCostCentres: pressCandidates([803, 4656, 5351]),
            autoSelectsCheapest: false,
          },
          {
            lineNumber: 6,
            processTypeId: 36,
            processTypeName: "Guillotine",
            isRequired: false,
            isDefault: true,
            defaultCostCentre: { costCentreId: 801, code: "FIN-GUILLOTINE" },
            candidateCostCentres: pressCandidates([801]),
            autoSelectsCheapest: false,
          },
          {
            lineNumber: 7,
            processTypeId: 30,
            processTypeName: "Folding",
            isRequired: false,
            isDefault: false,
            defaultCostCentre: { costCentreId: 812, code: "FIN-FOLD-TI52" },
            candidateCostCentres: pressCandidates([835]),
            autoSelectsCheapest: false,
          },
        ],
        pressesConsideredExampleSpec: "Cover, 170 gsm silk, 4/4",
        pressesConsidered: [
          pressConsidered(803, "chosen", "Cheapest of 2 suitable"),
          pressConsidered(
            4656,
            "suitable_not_chosen",
            "Suitable, higher cost per sheet",
          ),
          pressConsidered(
            5351,
            "excluded",
            "Sheet size below the press's minimum width gate",
          ),
        ],
      },
      {
        part: {
          productTypePartId: 2,
          partName: "Text",
          isNoPrinting: false,
          defaultOrientation: "—",
          fields: [
            field(
              "product_type_part",
              "is_no_printing",
              "No printing",
              // isNoPrinting is false for every part in this snapshot — say
              // so in words, not as a bare "No" next to a "No printing"
              // label (a double negative Henry would have to puzzle out).
              "Printed normally",
            ),
            field(
              "product_type_part",
              "default_printing_cost_centre_id",
              "Default printing cost centre",
              "—",
            ),
          ],
        },
        stepMap: [
          {
            lineNumber: 1,
            processTypeId: 53,
            processTypeName: "Litho Printing",
            isRequired: false,
            isDefault: false,
            defaultCostCentre: null,
            candidateCostCentres: pressCandidates([803, 4656, 5351]),
            autoSelectsCheapest: false,
          },
          {
            lineNumber: 8,
            processTypeId: 72,
            processTypeName: "Pre-Trim",
            isRequired: false,
            isDefault: false,
            defaultCostCentre: { costCentreId: 813, code: "FIN-DIECUT01" },
            candidateCostCentres: pressCandidates([813]),
            autoSelectsCheapest: false,
          },
          {
            lineNumber: 9,
            processTypeId: 36,
            processTypeName: "Guillotine",
            isRequired: true,
            isDefault: true,
            defaultCostCentre: { costCentreId: 801, code: "FIN-GUILLOTINE" },
            candidateCostCentres: pressCandidates([801]),
            autoSelectsCheapest: false,
          },
          {
            lineNumber: 11,
            processTypeId: 30,
            processTypeName: "Folding",
            isRequired: false,
            isDefault: false,
            defaultCostCentre: { costCentreId: 835, code: "FIN-FOLD-KH82" },
            candidateCostCentres: pressCandidates([835, 5274]),
            autoSelectsCheapest: false,
          },
        ],
        pressesConsideredExampleSpec: "Text, 130 gsm silk, 4/4",
        pressesConsidered: [
          pressConsidered(4656, "chosen", "Cheapest of 2 suitable"),
          pressConsidered(
            803,
            "suitable_not_chosen",
            "Suitable, higher cost per sheet",
          ),
        ],
      },
    ],
  },
  {
    productTypeId: 36,
    _self: "/views/manufacturing/product-types/36",
    type: "product_type",
    priceChecksUrl: "/views/live-costing/product-types?product_type_id=36",
    priceChecksUsedIn: productTypePriceChecksUsedIn(36),
    name: "Perfect Bound Book",
    isReplicated: true,
    notReplicatedReason: null,
    orderExpensePence: 750,
    markupMinPct: 5,
    markupMaxPct: 20,
    sync: SYNC.productTypes,
    parts: [
      {
        part: {
          productTypePartId: 61,
          partName: "Cover",
          isNoPrinting: false,
          defaultOrientation: "—",
          fields: [
            field(
              "product_type_part",
              "is_no_printing",
              "No printing",
              // isNoPrinting is false for every part in this snapshot — say
              // so in words, not as a bare "No" next to a "No printing"
              // label (a double negative Henry would have to puzzle out).
              "Printed normally",
            ),
            field(
              "product_type_part",
              "default_printing_cost_centre_id",
              "Default printing cost centre",
              "5351",
            ),
          ],
        },
        // Verbatim worked example from the design note
        // (03_manufacturing_surfaces_design.md, "Setup — list and detail shape").
        stepMap: [
          {
            lineNumber: 1,
            processTypeId: 53,
            processTypeName: "Litho Printing",
            isRequired: true,
            isDefault: false,
            defaultCostCentre: { costCentreId: 5351, code: "5COLXL75LEUV" },
            candidateCostCentres: pressCandidates([
              803, 5351, 5830, 1117, 5627,
            ]),
            autoSelectsCheapest: true,
          },
          {
            lineNumber: 6,
            processTypeId: 36,
            processTypeName: "Guillotine",
            isRequired: false,
            isDefault: true,
            defaultCostCentre: { costCentreId: 801, code: "FIN-GUILLOTINE" },
            candidateCostCentres: pressCandidates([801]),
            autoSelectsCheapest: false,
          },
          {
            lineNumber: 9,
            processTypeId: 65,
            processTypeName: "Perfect Binding",
            isRequired: true,
            isDefault: true,
            defaultCostCentre: {
              costCentreId: 5317,
              code: "HORIZONBQ470 DIGI",
            },
            candidateCostCentres: pressCandidates([5317]),
            autoSelectsCheapest: false,
          },
        ],
        pressesConsideredExampleSpec: "Cover, 250 gsm silk, 4/0",
        pressesConsidered: [
          pressConsidered(
            5351,
            "chosen",
            "Cheapest of 3 suitable; binder at Exeter",
          ),
          pressConsidered(
            803,
            "suitable_not_chosen",
            "Suitable, higher cost per sheet",
          ),
          pressConsidered(
            5830,
            "excluded",
            "Cross-site from the chosen binder",
          ),
          pressConsidered(
            1117,
            "excluded",
            "Sheet size above the press's maximum width gate",
          ),
          pressConsidered(
            5627,
            "excluded",
            "Cross-site from the chosen binder",
          ),
        ],
      },
      {
        part: {
          productTypePartId: 62,
          partName: "Text",
          isNoPrinting: false,
          defaultOrientation: "—",
          fields: [
            field(
              "product_type_part",
              "is_no_printing",
              "No printing",
              // isNoPrinting is false for every part in this snapshot — say
              // so in words, not as a bare "No" next to a "No printing"
              // label (a double negative Henry would have to puzzle out).
              "Printed normally",
            ),
          ],
        },
        stepMap: [
          {
            lineNumber: 1,
            processTypeId: 53,
            processTypeName: "Litho Printing",
            isRequired: false,
            isDefault: false,
            defaultCostCentre: null,
            candidateCostCentres: pressCandidates([803, 4656, 5351]),
            autoSelectsCheapest: false,
          },
          {
            lineNumber: 7,
            processTypeId: 17,
            processTypeName: "Collating - Sheets",
            isRequired: false,
            isDefault: false,
            defaultCostCentre: null,
            candidateCostCentres: [],
            autoSelectsCheapest: false,
          },
          {
            lineNumber: 8,
            processTypeId: 72,
            processTypeName: "Pre-Trim",
            isRequired: true,
            isDefault: false,
            defaultCostCentre: { costCentreId: 813, code: "FIN-DIECUT01" },
            candidateCostCentres: pressCandidates([813]),
            autoSelectsCheapest: false,
          },
          {
            lineNumber: 9,
            processTypeId: 36,
            processTypeName: "Guillotine",
            isRequired: true,
            isDefault: true,
            defaultCostCentre: { costCentreId: 801, code: "FIN-GUILLOTINE" },
            candidateCostCentres: pressCandidates([801]),
            autoSelectsCheapest: false,
          },
          {
            lineNumber: 11,
            processTypeId: 30,
            processTypeName: "Folding",
            isRequired: false,
            isDefault: false,
            defaultCostCentre: { costCentreId: 835, code: "FIN-FOLD-KH82" },
            candidateCostCentres: pressCandidates([835, 5274]),
            autoSelectsCheapest: false,
          },
        ],
        pressesConsideredExampleSpec: "Text, 100 gsm uncoated, 4/4",
        pressesConsidered: [
          pressConsidered(803, "chosen", "Cheapest of 2 suitable"),
          pressConsidered(
            4656,
            "suitable_not_chosen",
            "Suitable, higher cost per sheet",
          ),
        ],
      },
    ],
  },
  {
    productTypeId: 44,
    _self: "/views/manufacturing/product-types/44",
    type: "product_type",
    priceChecksUrl: "/views/live-costing/product-types?product_type_id=44",
    priceChecksUsedIn: productTypePriceChecksUsedIn(44),
    name: "Wide format panels",
    isReplicated: true,
    notReplicatedReason: null,
    orderExpensePence: 750,
    markupMinPct: null,
    markupMaxPct: null,
    sync: SYNC.productTypes,
    parts: [
      {
        part: {
          productTypePartId: 76,
          partName: "Panel",
          isNoPrinting: false,
          defaultOrientation: "—",
          fields: [
            field(
              "product_type_part",
              "is_no_printing",
              "No printing",
              // isNoPrinting is false for every part in this snapshot — say
              // so in words, not as a bare "No" next to a "No printing"
              // label (a double negative Henry would have to puzzle out).
              "Printed normally",
            ),
          ],
        },
        stepMap: [
          {
            lineNumber: 1,
            processTypeId: 110,
            processTypeName: "Large Format",
            isRequired: false,
            isDefault: false,
            defaultCostCentre: {
              costCentreId: 5608,
              code: "HYBRID SHEET WHITE",
            },
            candidateCostCentres: pressCandidates([5607, 5609]),
            autoSelectsCheapest: false,
          },
          {
            lineNumber: 2,
            processTypeId: 139,
            processTypeName: "Cut to Shape",
            isRequired: false,
            isDefault: true,
            defaultCostCentre: { costCentreId: 4639, code: "DIECUTXY" },
            candidateCostCentres: pressCandidates([4639]),
            autoSelectsCheapest: false,
          },
          {
            lineNumber: 4,
            processTypeId: 126,
            processTypeName: "Laser Cutting",
            isRequired: false,
            isDefault: false,
            defaultCostCentre: null,
            candidateCostCentres: [],
            autoSelectsCheapest: false,
          },
        ],
        pressesConsideredExampleSpec: "Panel, 5mm foamex, digital print",
        pressesConsidered: [
          pressConsidered(5607, "chosen", "Cheapest of 2 suitable"),
          pressConsidered(5830, "excluded", "Not gated for rigid substrate"),
        ],
      },
    ],
  },
  {
    productTypeId: 63,
    _self: "/views/manufacturing/product-types/63",
    type: "product_type",
    priceChecksUrl: "/views/live-costing/product-types?product_type_id=63",
    priceChecksUsedIn: productTypePriceChecksUsedIn(63),
    name: "Business cards",
    isReplicated: true,
    notReplicatedReason: null,
    orderExpensePence: 0,
    markupMinPct: null,
    markupMaxPct: null,
    sync: SYNC.productTypes,
    parts: [
      {
        part: {
          productTypePartId: 100,
          partName: "Front",
          isNoPrinting: false,
          defaultOrientation: "—",
          fields: [
            field(
              "product_type_part",
              "is_no_printing",
              "No printing",
              // isNoPrinting is false for every part in this snapshot — say
              // so in words, not as a bare "No" next to a "No printing"
              // label (a double negative Henry would have to puzzle out).
              "Printed normally",
            ),
          ],
        },
        stepMap: [
          {
            lineNumber: 1,
            processTypeId: 24,
            processTypeName: "Digital Printing",
            isRequired: true,
            isDefault: false,
            defaultCostCentre: {
              costCentreId: 5830,
              code: "BU-PRE-RICOHC9500",
            },
            candidateCostCentres: pressCandidates([1117, 5627, 5729, 5830]),
            autoSelectsCheapest: true,
          },
          {
            lineNumber: 3,
            processTypeId: 125,
            processTypeName: "Duplexing",
            isRequired: true,
            isDefault: true,
            defaultCostCentre: { costCentreId: 5274, code: "AMTMANN DUPLEX" },
            candidateCostCentres: pressCandidates([5274]),
            autoSelectsCheapest: false,
          },
          {
            lineNumber: 4,
            processTypeId: 36,
            processTypeName: "Guillotine",
            isRequired: true,
            isDefault: true,
            defaultCostCentre: { costCentreId: 801, code: "FIN-GUILLOTINE" },
            candidateCostCentres: pressCandidates([801]),
            autoSelectsCheapest: false,
          },
        ],
        pressesConsideredExampleSpec: "Front, 400 gsm silk duplex, digital",
        pressesConsidered: [
          pressConsidered(5830, "chosen", "Cheapest of 3 suitable"),
          pressConsidered(
            5627,
            "suitable_not_chosen",
            "Suitable, higher click rate",
          ),
          pressConsidered(
            5729,
            "suitable_not_chosen",
            "Suitable, higher click rate",
          ),
          pressConsidered(
            1117,
            "excluded",
            "Cross-site from the chosen duplexer",
          ),
        ],
      },
      {
        part: {
          productTypePartId: 101,
          partName: "Back",
          isNoPrinting: false,
          defaultOrientation: "—",
          fields: [
            field(
              "product_type_part",
              "is_no_printing",
              "No printing",
              // isNoPrinting is false for every part in this snapshot — say
              // so in words, not as a bare "No" next to a "No printing"
              // label (a double negative Henry would have to puzzle out).
              "Printed normally",
            ),
          ],
        },
        stepMap: [
          {
            lineNumber: 1,
            processTypeId: 24,
            processTypeName: "Digital Printing",
            isRequired: true,
            isDefault: false,
            defaultCostCentre: {
              costCentreId: 5830,
              code: "BU-PRE-RICOHC9500",
            },
            candidateCostCentres: pressCandidates([1117, 5627, 5729, 5830]),
            autoSelectsCheapest: true,
          },
          {
            lineNumber: 3,
            processTypeId: 36,
            processTypeName: "Guillotine",
            isRequired: true,
            isDefault: true,
            defaultCostCentre: { costCentreId: 801, code: "FIN-GUILLOTINE" },
            candidateCostCentres: pressCandidates([801]),
            autoSelectsCheapest: false,
          },
        ],
        pressesConsideredExampleSpec: "Back, 400 gsm silk duplex, digital",
        pressesConsidered: [
          pressConsidered(
            5830,
            "chosen",
            "Cheapest of 3 suitable — combined with Front at Duplexing",
          ),
          pressConsidered(
            5627,
            "suitable_not_chosen",
            "Suitable, higher click rate",
          ),
          pressConsidered(
            5729,
            "suitable_not_chosen",
            "Suitable, higher click rate",
          ),
        ],
      },
    ],
  },
  {
    productTypeId: 77,
    _self: "/views/manufacturing/product-types/77",
    type: "product_type",
    priceChecksUrl: "/views/live-costing/product-types?product_type_id=77",
    priceChecksUsedIn: productTypePriceChecksUsedIn(77),
    name: "Letterheads",
    isReplicated: false,
    notReplicatedReason:
      "Tharstern has this product type, but its parts and step map haven't synced into this standards snapshot yet — see 99_outstanding_setup.md §1a.",
    orderExpensePence: 0,
    markupMinPct: null,
    markupMaxPct: null,
    sync: SYNC.productTypes,
    parts: [],
  },
  // Referenced by Live costing's dict-greeting-cards fixture
  // (cost-engine-validation.ts) — added so that item's "Greeting cards ·
  // PT 91" is a real product type here too, not a dead reference (Read it
  // as Henry: mock data internally consistent across pages).
  {
    productTypeId: 91,
    _self: "/views/manufacturing/product-types/91",
    type: "product_type",
    priceChecksUrl: "/views/live-costing/product-types?product_type_id=91",
    priceChecksUsedIn: productTypePriceChecksUsedIn(91),
    name: "Greeting cards",
    isReplicated: false,
    notReplicatedReason:
      "Tharstern has this product type, but its parts and step map haven't synced into this standards snapshot yet — see 99_outstanding_setup.md §1a.",
    orderExpensePence: 0,
    markupMinPct: null,
    markupMaxPct: null,
    sync: SYNC.productTypes,
    parts: [],
  },
];

const PRODUCT_TYPE_SORT_FNS: Record<
  string,
  (e: ProductTypeIndexEntry) => string | number
> = {
  name: (e) => e.name,
  productTypeId: (e) => e.productTypeId,
};

function productTypeIndexEntry(pt: ProductTypeDetail): ProductTypeIndexEntry {
  return {
    productTypeId: pt.productTypeId,
    _self: pt._self,
    type: pt.type,
    priceChecksUrl: pt.priceChecksUrl,
    name: pt.name,
    orderExpensePence: pt.orderExpensePence,
    partCount: pt.parts.length,
    isReplicated: pt.isReplicated,
  };
}

export function listMockManufacturingProductTypes(
  options?: ProductTypeListOptions,
): ListResponse<ProductTypeIndexEntry> {
  let entries = PRODUCT_TYPES.map(productTypeIndexEntry);
  if (options?.q) {
    const needle = options.q.toLowerCase();
    entries = entries.filter((e) => e.name.toLowerCase().includes(needle));
  }
  if (options?.isReplicated != null) {
    entries = entries.filter((e) => e.isReplicated === options.isReplicated);
  }
  entries = sortBy(entries, options?.sort, PRODUCT_TYPE_SORT_FNS, "name");
  return paginate(entries, options);
}

export function getMockManufacturingProductType(
  productTypeId: number,
): ProductTypeDetail | null {
  return PRODUCT_TYPES.find((p) => p.productTypeId === productTypeId) ?? null;
}

// ── Materials ───────────────────────────────────────────────────────────────

interface RawMaterial {
  code: string;
  productId: number;
  /** Tharstern's own product-catalogue classification (`Product.Group` /
   *  `Product.Type`) — what the catalogue's option resolution matches on
   *  (see the design note). "—" where the oracle standards snapshot this
   *  fixture set is drawn from didn't capture the column, an honest gap
   *  rather than a fabricated classification. */
  group: string;
  type: string;
  weightGsm: number;
  calliperMicrons: number;
  /** Which of weightGsm/calliperMicrons is this material's headline spec —
   *  paper stock is specified by weight, board and film by thickness. */
  unit: "gsm" | "microns";
  sizes: {
    widthMm: number;
    depthMm: number;
    pricePence: number | null;
    packQuantity: number;
  }[];
}

const RAW_MATERIALS: RawMaterial[] = [
  {
    code: "ANT064_617674",
    productId: 921,
    group: "—",
    type: "—",
    weightGsm: 84,
    calliperMicrons: 84,
    unit: "gsm",
    sizes: [
      { widthMm: 450, depthMm: 320, pricePence: 7149, packQuantity: 1000 },
    ],
  },
  {
    code: "ANT064_609213",
    productId: 3790,
    group: "—",
    type: "—",
    weightGsm: 70,
    calliperMicrons: 90,
    unit: "gsm",
    sizes: [
      { widthMm: 520, depthMm: 720, pricePence: 2862, packQuantity: 500 },
    ],
  },
  {
    code: "ANT064_617394",
    productId: 4265,
    group: "—",
    type: "—",
    weightGsm: 600,
    calliperMicrons: 3000,
    unit: "gsm",
    sizes: [
      { widthMm: 1062, depthMm: 1575, pricePence: 276000, packQuantity: 50 },
    ],
  },
  {
    code: "ANT064_617667",
    productId: 4812,
    group: "—",
    type: "—",
    weightGsm: 80,
    calliperMicrons: 80,
    unit: "gsm",
    sizes: [
      { widthMm: 450, depthMm: 320, pricePence: 5651, packQuantity: 1000 },
    ],
  },
  {
    code: "ANT064_609308",
    productId: 4866,
    group: "—",
    type: "—",
    weightGsm: 150,
    calliperMicrons: 174,
    unit: "gsm",
    sizes: [
      { widthMm: 450, depthMm: 640, pricePence: 4628, packQuantity: 250 },
    ],
  },
  {
    code: "ANT064_617662",
    productId: 5469,
    group: "—",
    type: "—",
    weightGsm: 170,
    calliperMicrons: 170,
    unit: "gsm",
    sizes: [
      { widthMm: 450, depthMm: 320, pricePence: 9754, packQuantity: 500 },
    ],
  },
  {
    code: "DEN068_PAS250SR1 FSC",
    productId: 14542,
    group: "—",
    type: "—",
    weightGsm: 250,
    calliperMicrons: 239,
    unit: "gsm",
    sizes: [
      { widthMm: 640, depthMm: 900, pricePence: 21066, packQuantity: 125 },
    ],
  },
  {
    code: "DG581500",
    productId: 16925,
    group: "—",
    type: "—",
    weightGsm: 900,
    calliperMicrons: 1500,
    unit: "gsm",
    sizes: [
      { widthMm: 900, depthMm: 640, pricePence: 121682, packQuantity: 42 },
    ],
  },
  {
    code: "ELL039_195_22535",
    productId: 15407,
    group: "—",
    type: "—",
    weightGsm: 225,
    calliperMicrons: 280,
    unit: "gsm",
    sizes: [
      { widthMm: 520, depthMm: 775, pricePence: 35152, packQuantity: 250 },
    ],
  },
  {
    code: "PRE067_AEWS46250",
    productId: 11063,
    group: "—",
    type: "—",
    weightGsm: 250,
    calliperMicrons: 300,
    unit: "gsm",
    sizes: [
      { widthMm: 320, depthMm: 464, pricePence: 11307, packQuantity: 250 },
    ],
  },
  {
    code: "ANT_DILITE_MMW_3MM",
    productId: 52831,
    group: "—",
    type: "—",
    weightGsm: 3500,
    // A rigid composite board (Dilite) — specified by thickness, not
    // weight, so its headline spec is the calliper, in microns.
    calliperMicrons: 3000,
    unit: "microns",
    sizes: [
      { widthMm: 1250, depthMm: 2500, pricePence: 6919044, packQuantity: 1 },
      // A real second size row with no matching pricing row in this
      // snapshot — an honest "not yet replicated" gap on this one value,
      // not a fabricated price.
      { widthMm: 1500, depthMm: 3050, pricePence: null, packQuantity: 1 },
    ],
  },
  {
    // WINWINWIN is the house-brand paper group used elsewhere in the
    // catalogue (mock-adapter.ts's "WINWINWIN House Papers") — reused
    // verbatim here so this material and the
    // dictionary item options that resolve against it can never disagree
    // (Read it as Henry: mock data internally consistent across pages).
    code: "WWW-GLS-150",
    productId: 61204,
    group: "WINWINWIN",
    type: "WWW-GLS",
    weightGsm: 150,
    calliperMicrons: 138,
    unit: "gsm",
    sizes: [
      { widthMm: 450, depthMm: 640, pricePence: 3980, packQuantity: 250 },
      { widthMm: 640, depthMm: 900, pricePence: 7845, packQuantity: 250 },
    ],
  },
  {
    // A reel-fed self-adhesive vinyl — no group/type in the snapshot (the
    // same honest gap as the sheet papers above), specified by film
    // thickness in microns rather than weight, and with no sheet sizes:
    // it is sold and cut from the roll, not as pre-cut sheets, so the
    // honest "no sheet sizes captured" empty state is the correct one,
    // not a fabricated roll length.
    code: "SAV-GLOSS-WHITE-100",
    productId: 73512,
    group: "—",
    type: "—",
    weightGsm: 130,
    calliperMicrons: 100,
    unit: "microns",
    sizes: [],
  },
];

function materialSheetSizes(raw: RawMaterial): MaterialSheetSize[] {
  return raw.sizes.map((s) => ({
    code: raw.code,
    widthMm: s.widthMm,
    depthMm: s.depthMm,
    pricePence: s.pricePence ?? 0,
    pricingType: "/1000",
    packQuantity: s.packQuantity,
    useFullPack: false,
    minChargePence: null,
    isAvailable: true,
  }));
}

function materialIndexEntry(raw: RawMaterial): MaterialIndexEntry {
  return {
    code: raw.code,
    description: raw.code,
    group: raw.group,
    type: raw.type,
    gsmOrMicrons: raw.unit === "microns" ? raw.calliperMicrons : raw.weightGsm,
    gsmOrMicronsUnit: raw.unit,
    sheetSizeCount: raw.sizes.length,
    isPlateOrBox: false,
  };
}

const MATERIAL_SORT_FNS: Record<
  string,
  (e: MaterialIndexEntry) => string | number
> = {
  code: (e) => e.code,
  gsmOrMicrons: (e) => e.gsmOrMicrons,
};

export function listMockManufacturingMaterials(
  options?: MaterialListOptions,
): ListResponse<MaterialIndexEntry> {
  let entries = RAW_MATERIALS.map(materialIndexEntry);
  if (options?.q) {
    const needle = options.q.toLowerCase();
    entries = entries.filter((e) => e.code.toLowerCase().includes(needle));
  }
  entries = sortBy(entries, options?.sort, MATERIAL_SORT_FNS, "code");
  return paginate(entries, options);
}

export function getMockManufacturingMaterial(
  code: string,
): MaterialDetail | null {
  const raw = RAW_MATERIALS.find((m) => m.code === code);
  if (!raw) return null;
  return {
    ...materialIndexEntry(raw),
    sync: SYNC.materials,
    calliperMicrons: raw.calliperMicrons,
    sheetSizes: materialSheetSizes(raw),
    fields: [
      field("material", "weight", "Weight (gsm)", `${raw.weightGsm} gsm`),
      field(
        "material",
        "calliper",
        "Calliper",
        `${raw.calliperMicrons} microns`,
      ),
      field("material", "pricing_type", "Pricing basis", "per 1,000"),
      field("material", "group", "Group", raw.group),
      field("material", "type", "Type", raw.type),
      field("material", "inactive", "Availability", "Available"),
    ],
  };
}

// ── Outwork ─────────────────────────────────────────────────────────────────

interface RawOutwork {
  outworkId: number;
  code: string;
  supplierCode: string;
  minChargePence: number;
  additionalChargePence: number;
  bands: { sizeCode: string; pricePence: number }[];
}

const RAW_OUTWORK: RawOutwork[] = [
  {
    outworkId: 429,
    code: "EX-PRO-ANTI-SCUFF-LAM-01ANTI SC",
    supplierCode: "FIN014",
    minChargePence: 10000,
    additionalChargePence: 0,
    bands: [
      { sizeCode: "340X720", pricePence: 13800 },
      { sizeCode: "B2", pricePence: 20400 },
      { sizeCode: "B3", pricePence: 11200 },
      { sizeCode: "SRA2", pricePence: 16800 },
      { sizeCode: "SRA3", pricePence: 9400 },
    ],
  },
  {
    outworkId: 432,
    code: "EX-PRO-FLOODUV-1-SIDE",
    supplierCode: "FIN014",
    minChargePence: 15100,
    additionalChargePence: 2100,
    bands: [
      { sizeCode: "340X720", pricePence: 10000 },
      { sizeCode: "B2", pricePence: 15100 },
      { sizeCode: "SRA2", pricePence: 12400 },
      { sizeCode: "SRA3", pricePence: 9200 },
    ],
  },
  {
    outworkId: 434,
    code: "EX-GLOSS-LAM-01",
    supplierCode: "FIN014",
    minChargePence: 5000,
    additionalChargePence: 0,
    bands: [
      { sizeCode: "340X720", pricePence: 5700 },
      { sizeCode: "B2", pricePence: 7700 },
      { sizeCode: "B3", pricePence: 5000 },
      { sizeCode: "SRA2", pricePence: 6200 },
      { sizeCode: "SRA3", pricePence: 4200 },
    ],
  },
  {
    outworkId: 446,
    code: "EX-SILK-LAM-01",
    supplierCode: "FIN014",
    minChargePence: 8000,
    additionalChargePence: 0,
    bands: [
      { sizeCode: "340X720", pricePence: 9300 },
      { sizeCode: "B2", pricePence: 13900 },
      { sizeCode: "B3", pricePence: 7900 },
      { sizeCode: "SRA2", pricePence: 10800 },
      { sizeCode: "SRA3", pricePence: 6900 },
    ],
  },
  {
    outworkId: 450,
    code: "EX-SPOTUV-10",
    supplierCode: "FIN014",
    minChargePence: 5500,
    additionalChargePence: 10100,
    bands: [
      { sizeCode: "340X720", pricePence: 5300 },
      { sizeCode: "B2", pricePence: 5700 },
      { sizeCode: "SRA2", pricePence: 5600 },
      { sizeCode: "SRA3", pricePence: 4800 },
    ],
  },
  {
    outworkId: 1162,
    code: "EX-MATTLAM-01",
    supplierCode: "FIN014",
    minChargePence: 5000,
    additionalChargePence: 0,
    bands: [
      { sizeCode: "340X720", pricePence: 5900 },
      { sizeCode: "B2", pricePence: 8800 },
      { sizeCode: "B3", pricePence: 6100 },
      { sizeCode: "SRA2", pricePence: 7700 },
      { sizeCode: "SRA3", pricePence: 4600 },
    ],
  },
];

function outworkEntry(raw: RawOutwork): OutworkChoiceEntry {
  return {
    outworkId: raw.outworkId,
    _self: `/views/manufacturing/outwork?outwork_id=${raw.outworkId}`,
    type: "outwork_choice",
    code: raw.code,
    supplierCode: raw.supplierCode,
    clickPricePence: 0,
    minChargePence: raw.minChargePence,
    additionalChargePence: raw.additionalChargePence,
    priceBands: raw.bands.map((b) => ({
      sizeCode: b.sizeCode,
      pricePence: b.pricePence,
    })),
  };
}

const OUTWORK_SORT_FNS: Record<
  string,
  (e: OutworkChoiceEntry) => string | number
> = {
  code: (e) => e.code,
  supplierCode: (e) => e.supplierCode,
};

export function listMockManufacturingOutworkChoices(
  options?: OutworkListOptions,
): ListResponse<OutworkChoiceEntry> {
  let entries = RAW_OUTWORK.map(outworkEntry);
  if (options?.q) {
    const needle = options.q.toLowerCase();
    entries = entries.filter((e) => e.code.toLowerCase().includes(needle));
  }
  if (options?.supplierCode) {
    entries = entries.filter((e) => e.supplierCode === options.supplierCode);
  }
  entries = sortBy(entries, options?.sort, OUTWORK_SORT_FNS, "code");
  return paginate(entries, options);
}

// ── Sites & process types ────────────────────────────────────────────────

function siteEntry(raw: Omit<SiteEntry, "_self" | "type">): SiteEntry {
  return {
    ...raw,
    _self: `/views/manufacturing/sites?site_id=${raw.siteId}`,
    type: "site",
  };
}

export function listMockManufacturingSites(
  options?: ListOptions,
): ListResponse<SiteEntry> {
  let entries = SITES.map(siteEntry);
  if (options?.q) {
    const needle = options.q.toLowerCase();
    entries = entries.filter(
      (e) =>
        e.code.toLowerCase().includes(needle) ||
        e.description.toLowerCase().includes(needle),
    );
  }
  return paginate(entries, options);
}

/** True for a printing process type (litho, digital, large/wide format) —
 *  the source of truth for "is this cost centre a press", derived from the
 *  process type's own `kind` flag rather than an id allowlist (coordinator
 *  review round 6 — an id allowlist silently missed Large Format/Wide
 *  Format Printing, so the hybrid presses never showed as excluded). */
export function isPrintingProcessType(processTypeId: number): boolean {
  return PROCESS_TYPES.some(
    (p) => p.processTypeId === processTypeId && p.kind === "printing",
  );
}

/** True for the plate-making process type — origination's own cost head.
 *  A plate-making cost centre (e.g. 800 PRE-PLATEMAKING-B2) must never be
 *  bucketed as a finishing line just because it isn't a press. */
export function isOriginationProcessType(processTypeId: number): boolean {
  return processTypeId === 2;
}

function processTypeEntry(
  raw: Omit<ProcessTypeEntry, "_self" | "type">,
): ProcessTypeEntry {
  return {
    ...raw,
    _self: `/views/manufacturing/process-types?process_type_id=${raw.processTypeId}`,
    type: "process_type",
  };
}

export function listMockManufacturingProcessTypes(
  options?: ListOptions,
): ListResponse<ProcessTypeEntry> {
  let entries = PROCESS_TYPES.map(processTypeEntry);
  if (options?.q) {
    const needle = options.q.toLowerCase();
    entries = entries.filter((e) => e.name.toLowerCase().includes(needle));
  }
  entries = sortBy(
    entries,
    options?.sort,
    { name: (e) => e.name, costCentreCount: (e) => e.costCentreCount },
    "name",
  );
  return paginate(entries, options);
}

// ── Sync status per family (list-page "synced HH:MM from `Table`" line) ───

export const MANUFACTURING_SYNC_INFO = SYNC;
